

















import { useEffect, useRef, useState, useCallback } from "react";
import { useCallStore } from "../app/callStore";
import { getSocket } from "../socket/socket";
import { FILE_BASE_URL, getGroupMembersApi } from "../app/api";
import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2, Users, Volume2, VolumeX, ShieldCheck, MessageCircle, RefreshCw, Clock3, CheckCircle2, XCircle, Download, FileIcon } from "lucide-react";
import ChatInput from "../pages/chat/ChatInput";
import ringtoneSound from "../assets/VibrateCall.mp3";

const OUTGOING_CALL_TIMEOUT_MS = 20000;
const APP_ICON = "/favicon.svg";

function getCallAttachmentUrl(path = "") {
  if (!path) return "";
  return String(path).startsWith("http")
    ? path
    : `${FILE_BASE_URL}/${String(path).startsWith("/") ? String(path).slice(1) : path}`;
}

function getCallAttachmentName(path = "") {
  const cleanPath = String(path).split("?")[0];
  return cleanPath.split("/").pop() || "Attachment";
}

function isCallPreviewableFile(fileName = "") {
  return /\.(pdf|txt|csv|json|html?)$/i.test(fileName);
}

export default function CallManager() {
  const {
    isReceivingCall,
    isCalling,
    callActive,
    callerName,
    callerSignal,
    callerId,
    targetUserId,
    targetUserName,
    groupId,
    groupName,
    participants,
    isVideo,
    setCallState,
    clearCallState,
  } = useCallStore();

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null); // For 1-on-1
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [isBusyResponse, setIsBusyResponse] = useState(false);
  const [showCallEndedModal, setShowCallEndedModal] = useState(false);
  const [missedCallMessage, setMissedCallMessage] = useState("");
  const [callDuration, setCallDuration] = useState(0);
  const [groupCallMembers, setGroupCallMembers] = useState([]);
  const [callChatOpen, setCallChatOpen] = useState(false);
  const [callChatMessages, setCallChatMessages] = useState([]);
  const [callAttachmentPreview, setCallAttachmentPreview] = useState(null);

  const durationIntervalRef = useRef(null);
  const outgoingCallTimeoutRef = useRef(null);
  const groupNoAnswerTimersRef = useRef({});
  const callRootRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const connectionRef = useRef(null); // For 1-on-1
  const peersRef = useRef({}); // For Group: { userId: RTCPeerConnection }
  
  const ringtoneRef = useRef(null);
  const vibrateInterval = useRef(null);
  const isRingingRef = useRef(false);
  const callInitiatedRef = useRef(false);
  const incomingNotificationRef = useRef(null);
  const titleFlashIntervalRef = useRef(null);
  const originalTitleRef = useRef(document.title);

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdminUser = [1, 2, 3].includes(Number(currentUser?.role_id));

  const stopIncomingCallAlert = useCallback(() => {
    incomingNotificationRef.current?.close?.();
    incomingNotificationRef.current = null;

    if (titleFlashIntervalRef.current) {
      clearInterval(titleFlashIntervalRef.current);
      titleFlashIntervalRef.current = null;
    }

    document.title = originalTitleRef.current;
    navigator.clearAppBadge?.()?.catch?.(() => {});
  }, []);

  const startTitleFlash = useCallback((name) => {
    if (titleFlashIntervalRef.current) return;

    const callTitle = `Incoming call - ${name || "Uptask"}`;
    titleFlashIntervalRef.current = setInterval(() => {
      document.title = document.title === callTitle
        ? originalTitleRef.current
        : callTitle;
    }, 900);
  }, []);

  const showIncomingCallAlert = useCallback(({ name, isVideo: videoCall, isGroup = false }) => {
    const displayName = name || (isGroup ? "Group call" : "Unknown caller");
    startTitleFlash(displayName);
    navigator.setAppBadge?.(1)?.catch?.(() => {});

    if (!("Notification" in window) || Notification.permission !== "granted") {
      return;
    }

    incomingNotificationRef.current?.close?.();
    try {
      const notification = new Notification(
        isGroup ? `Incoming group call: ${displayName}` : `Incoming call from ${displayName}`,
        {
          body: videoCall ? "Video call ringing in Uptask" : "Voice call ringing in Uptask",
          icon: APP_ICON,
          badge: APP_ICON,
          tag: isGroup ? "uptask-incoming-group-call" : "uptask-incoming-call",
          renotify: true,
          requireInteraction: true,
          silent: false,
        }
      );

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      incomingNotificationRef.current = notification;
    } catch (err) {
      console.warn("Unable to show incoming call notification:", err);
    }
  }, [startTitleFlash]);

  // ==========================================
  // 🎵 HELPERS
  // ==========================================

  const startRinging = useCallback(() => {
    if (!ringtoneRef.current) {
      ringtoneRef.current = new Audio(ringtoneSound);
      ringtoneRef.current.loop = true;
    }
    if (isRingingRef.current) return;
    isRingingRef.current = true;

    ringtoneRef.current.play().then(() => {
      setAutoplayBlocked(false);
    }).catch(e => {
      console.log("Audio block:", e);
      setAutoplayBlocked(true);
    });
    if (navigator.vibrate) {
      if (vibrateInterval.current) {
        clearInterval(vibrateInterval.current);
      }
      vibrateInterval.current = setInterval(() => {
        navigator.vibrate([400, 200, 400]);
      }, 1200);
    }
  }, []);

  const stopRinging = useCallback(() => {
    isRingingRef.current = false;
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    if (vibrateInterval.current) {
      clearInterval(vibrateInterval.current);
      vibrateInterval.current = null;
    }
    if (navigator.vibrate) navigator.vibrate(0);
  }, []);

  const clearOutgoingCallTimeout = useCallback(() => {
    if (outgoingCallTimeoutRef.current) {
      clearTimeout(outgoingCallTimeoutRef.current);
      outgoingCallTimeoutRef.current = null;
    }
  }, []);

  const clearGroupNoAnswerTimers = useCallback(() => {
    Object.values(groupNoAnswerTimersRef.current).forEach(clearTimeout);
    groupNoAnswerTimersRef.current = {};
  }, []);

  const normalizeGroupMember = useCallback((member) => {
    const userId = member.user_id ?? member.userId ?? member.id ?? member.employee_id;
    const firstName = member.first_name ?? member.firstName ?? "";
    const lastName = member.last_name ?? member.lastName ?? "";
    const name = member.name || `${firstName} ${lastName}`.trim() || `User ${userId}`;

    return {
      userId,
      name,
      status: String(userId) === String(currentUser.id) ? "joined" : "ringing",
      stream: null,
    };
  }, [currentUser.id]);

  const updateMemberStatus = useCallback((userId, status, extra = {}) => {
    setGroupCallMembers((prev) =>
      prev.map((member) =>
        String(member.userId) === String(userId)
          ? { ...member, status, ...extra }
          : member
      )
    );
  }, []);

  const startNoAnswerTimer = useCallback((member) => {
    if (!member?.userId || String(member.userId) === String(currentUser.id)) return;

    if (groupNoAnswerTimersRef.current[member.userId]) {
      clearTimeout(groupNoAnswerTimersRef.current[member.userId]);
    }

    groupNoAnswerTimersRef.current[member.userId] = setTimeout(() => {
      updateMemberStatus(member.userId, "missed");
    }, OUTGOING_CALL_TIMEOUT_MS);
  }, [currentUser.id, updateMemberStatus]);

  const loadGroupCallMembers = useCallback(async (targetGroupId, shouldStartTimers = false) => {
    if (!targetGroupId) return;

    try {
      const res = await getGroupMembersApi(targetGroupId);
      const members = res?.members || res?.data || [];
      const normalizedMembers = members.map(normalizeGroupMember);

      setGroupCallMembers(normalizedMembers);

      if (shouldStartTimers) {
        clearGroupNoAnswerTimers();
        normalizedMembers.forEach(startNoAnswerTimer);
      }
    } catch (err) {
      console.error("Failed to load group call members", err);
    }
  }, [clearGroupNoAnswerTimers, normalizeGroupMember, startNoAnswerTimer]);

  const stopMediaStream = useCallback((stream) => {
    if (!stream) return;
    stream.getTracks().forEach((track) => {
      track.enabled = false;
      track.stop();
    });
  }, []);

  const detachMediaElements = useCallback(() => {
    [localVideoRef.current, remoteVideoRef.current, remoteAudioRef.current]
      .filter(Boolean)
      .forEach((element) => {
        element.pause?.();
        element.srcObject = null;
        element.removeAttribute?.("src");
        element.load?.();
      });

    callRootRef.current
      ?.querySelectorAll("audio, video")
      .forEach((element) => {
        element.pause?.();
        element.srcObject = null;
        element.removeAttribute?.("src");
        element.load?.();
      });
  }, []);

  const initStream = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      stream.getVideoTracks().forEach(track => {
        track.enabled = true;
      });

      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (err) {
      console.error("Stream access denied:", err);
      return null;
    }
  }, []);

  const createPeer = useCallback((stream, socket, toUserId) => {
    const peer = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
    });
  
    stream.getTracks().forEach(track => {
      peer.addTrack(track, stream);
    });
  
    peer.ontrack = (event) => {
      console.log("🎬 [1-on-1] Remote track received:", event.track.kind, event.streams);
      if (event.streams && event.streams[0]) {
        event.streams[0].getTracks().forEach(track => {
          track.enabled = true;
          console.log(`📡 Track ${track.kind} enabled`);
        });
        setRemoteStream(event.streams[0]);
      }
    };

    peer.onicecandidate = (event) => {
      if (event.candidate) {
        console.log("❄️ [1-on-1] ICE Candidate generated");
        socket.emit("ice_candidate", {
          to: toUserId,
          candidate: event.candidate
        });
      }
    };
  
    return peer;
  }, []);

  const endCallCleanup = useCallback((showModal = false) => {
    clearOutgoingCallTimeout();
    clearGroupNoAnswerTimers();
    stopIncomingCallAlert();
    stopRinging();

    stopMediaStream(localStream);
    stopMediaStream(remoteStream);
    useCallStore.getState().participants.forEach((participant) => {
      stopMediaStream(participant.stream);
    });

    detachMediaElements();

    if (connectionRef.current) {
      connectionRef.current.close();
      connectionRef.current = null;
    }
    
    // Cleanup Group Peers
    Object.values(peersRef.current).forEach(peer => peer.close());
    peersRef.current = {};

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    setLocalStream(null);
    setRemoteStream(null);
    setCallDuration(0);
    setIsMinimized(false);
    setGroupCallMembers([]);
    setCallChatOpen(false);
    setCallChatMessages([]);
    clearCallState();
    callInitiatedRef.current = false;
    
    if (showModal) {
      setShowCallEndedModal(true);
      setTimeout(() => setShowCallEndedModal(false), 3000);
    }
  }, [
    localStream,
    remoteStream,
    clearCallState,
    clearGroupNoAnswerTimers,
    clearOutgoingCallTimeout,
    detachMediaElements,
    stopMediaStream,
    stopIncomingCallAlert,
    stopRinging,
  ]);

  // ==========================================
  // 📡 WEBRTC GROUP MESH HELPERS
  // ==========================================

  const createGroupPeer = useCallback((targetUserId, stream, socket) => {
    const peer = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
    });

    stream.getTracks().forEach(track => peer.addTrack(track, stream));

    peer.ontrack = (event) => {
      console.log(`🎬 Received track from ${targetUserId}`);
      updateMemberStatus(targetUserId, "joined", { stream: event.streams[0] });
      setCallState({
        participants: useCallStore.getState().participants.map(p => 
          p.userId === targetUserId ? { ...p, stream: event.streams[0] } : p
        )
      });
    };

    peer.onicecandidate = (event) => {
      if (event.candidate) {
        socket.emit("webrtc_ice_candidate", {
          targetUserId,
          candidate: event.candidate
        });
      }
    };

    return peer;
  }, [setCallState, updateMemberStatus]);

  // ==========================================
  // 🧨 EFFECTS
  // ==========================================

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleIncomingCall = (data) => {
      if (isReceivingCall || isCalling || callActive) {
        socket.emit("call_busy", { to: data.from });
        return;
      }
      setCallState({
        isReceivingCall: true,
        callerId: data.from,
        callerName: data.name,
        callerSignal: data.signal,
        isVideo: data.isVideo,
      });
      showIncomingCallAlert({
        name: data.name,
        isVideo: data.isVideo,
        isGroup: false,
      });
      startRinging();
    };

    const handleIncomingGroupCall = (data) => {
      if (isReceivingCall || isCalling || callActive) {
        // socket.emit("call_busy", { to: data.from });
        return;
      }
      setCallState({
        isReceivingCall: true,
        groupId: data.groupId,
        groupName: data.groupName,
        callerId: data.from,
        callerName: data.senderName,
        isVideo: data.isVideo
      });
      showIncomingCallAlert({
        name: data.groupName || data.senderName,
        isVideo: data.isVideo,
        isGroup: true,
      });
      loadGroupCallMembers(data.groupId, false);
      startRinging();
    };

    const handleGroupCallStarted = (data) => {
      const activeCalls = useCallStore.getState().activeGroupCalls;
      if (!activeCalls.find(c => c.groupId === data.groupId)) {
        setCallState({
          activeGroupCalls: [...activeCalls, data]
        });
      }
      
      // Also trigger incoming call modal if we are a member
      handleIncomingGroupCall(data);
    };

    const handleGroupCallEnded = (payload = {}) => {
      const endedGroupId = payload.groupId ?? payload.group_id ?? payload.id;
      const callState = useCallStore.getState();
      const isCurrentGroupCall = endedGroupId
        ? String(callState.groupId) === String(endedGroupId)
        : Boolean(callState.groupId);

      const activeGroupCalls = useCallStore
        .getState()
        .activeGroupCalls
        .filter(c => endedGroupId && String(c.groupId) !== String(endedGroupId));

      setCallState({
        activeGroupCalls,
      });

      if (isCurrentGroupCall) {
        endCallCleanup(callState.callActive);
      }
    };

    const handleActiveGroupCalls = (calls) => {
      console.log("📡 Initial active group calls:", calls);
      setCallState({ activeGroupCalls: calls });

      const callState = useCallStore.getState();
      if (
        callState.groupId &&
        (callState.isReceivingCall || callState.isCalling) &&
        !calls.some(call => String(call.groupId) === String(callState.groupId))
      ) {
        stopIncomingCallAlert();
        stopRinging();
        clearCallState();
      }
    };

    const handleUserBusy = () => {
      setIsBusyResponse(true);
      setTimeout(() => setIsBusyResponse(false), 5000);
      endCallCleanup();
    };

    const handleCallAccepted = async (signal) => {
      console.log("✅ [1-on-1] Call Accepted");
      clearOutgoingCallTimeout();
      stopRinging();
      setCallState({ callActive: true, isCalling: false });
      if (connectionRef.current) {
        try {
          await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
          // Process queued ICE candidates
          while (iceCandidateQueue.current.length > 0) {
            const candidate = iceCandidateQueue.current.shift();
            await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          }
        } catch (e) {
          console.error("Error setting remote description:", e);
        }
      }
    };

    const handleCallRejected = () => {
      clearOutgoingCallTimeout();
      stopRinging();
      endCallCleanup();
    };

    const handleCallEnded = () => {
      console.log("☎️ Received call_ended from socket");
      clearOutgoingCallTimeout();
      endCallCleanup(true);
    };

    const handleSocketDisconnect = () => {
      const callState = useCallStore.getState();
      if (callState.isReceivingCall || callState.isCalling || callState.callActive) {
        endCallCleanup(callState.callActive);
      } else {
        stopIncomingCallAlert();
        stopRinging();
      }
    };

    const handleIceCandidate = async (candidate) => {
      if (connectionRef.current && candidate) {
        if (connectionRef.current.remoteDescription) {
          try {
            await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (e) {
            console.error("Error adding ice candidate:", e);
          }
        } else {
          iceCandidateQueue.current.push(candidate);
        }
      }
    };

    // --- GROUP CALL EVENTS ---
    const handleUserJoinedCall = async ({ userId, name }) => {
      console.log(`👤 User joined call: ${userId} (${name})`);
      if (groupNoAnswerTimersRef.current[userId]) {
        clearTimeout(groupNoAnswerTimersRef.current[userId]);
        delete groupNoAnswerTimersRef.current[userId];
      }
      updateMemberStatus(userId, "joined", { name: name || `User ${userId}` });
      const currentParticipants = useCallStore.getState().participants;
      if (!currentParticipants.find(p => p.userId === userId)) {
        setCallState({
          participants: [...currentParticipants, { userId, stream: null, name: name || `User ${userId}` }]
        });
      }
    };

    const handleUserLeftCall = ({ userId, name }) => {
      if (peersRef.current[userId]) {
        peersRef.current[userId].close();
        delete peersRef.current[userId];
      }
      updateMemberStatus(userId, "left", { name: name || `User ${userId}`, stream: null });
      setCallState({
        participants: useCallStore.getState().participants.filter(p => p.userId !== userId)
      });
      // Optionally show a notification
    };

    const handleExistingParticipants = ({ users }) => {
      console.log("👥 Existing participants:", users);
      users.forEach((user) => updateMemberStatus(user.userId, "joined", { name: user.name }));
      setCallState({
        participants: users.map(u => ({ userId: u.userId, stream: null, name: u.name }))
      });
    };

    const handleWebRTCOffer = async ({ from, signal }) => {
      const stream = localStream || await initStream();
      const peer = createGroupPeer(from, stream, socket);
      peersRef.current[from] = peer;
      await peer.setRemoteDescription(new RTCSessionDescription(signal));
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      socket.emit("webrtc_answer", { targetUserId: from, signal: answer });
    };

    const handleWebRTCAnswer = async ({ from, signal }) => {
      const peer = peersRef.current[from];
      if (peer) {
        await peer.setRemoteDescription(new RTCSessionDescription(signal));
      }
    };

    const handleWebRTCIce = async ({ from, candidate }) => {
      const peer = peersRef.current[from];
      if (peer && candidate) {
        await peer.addIceCandidate(new RTCIceCandidate(candidate));
      }
    };

    socket.on("incoming_call", handleIncomingCall);
    socket.on("incoming_group_call", handleIncomingGroupCall);
    socket.on("group_call_started", handleGroupCallStarted);
    socket.on("group_call_ended", handleGroupCallEnded);
    socket.on("active_group_calls", handleActiveGroupCalls);
    socket.on("user_busy", handleUserBusy);
    socket.on("call_accepted", handleCallAccepted);
    socket.on("call_rejected", handleCallRejected);
    socket.on("call_ended", handleCallEnded);
    socket.on("disconnect", handleSocketDisconnect);
    socket.on("ice_candidate", handleIceCandidate);

    socket.on("user_joined_call", handleUserJoinedCall);
    socket.on("user_left_call", handleUserLeftCall);
    socket.on("existing_participants", handleExistingParticipants);
    socket.on("webrtc_offer", handleWebRTCOffer);
    socket.on("webrtc_answer", handleWebRTCAnswer);
    socket.on("webrtc_ice_candidate", handleWebRTCIce);

    return () => {
      socket.off("incoming_call", handleIncomingCall);
      socket.off("incoming_group_call", handleIncomingGroupCall);
      socket.off("group_call_started", handleGroupCallStarted);
      socket.off("group_call_ended", handleGroupCallEnded);
      socket.off("active_group_calls", handleActiveGroupCalls);
      socket.off("user_busy", handleUserBusy);
      socket.off("call_accepted", handleCallAccepted);
      socket.off("call_rejected", handleCallRejected);
      socket.off("call_ended", handleCallEnded);
      socket.off("disconnect", handleSocketDisconnect);
      socket.off("ice_candidate", handleIceCandidate);

      socket.off("user_joined_call", handleUserJoinedCall);
      socket.off("user_left_call", handleUserLeftCall);
      socket.off("existing_participants", handleExistingParticipants);
      socket.off("webrtc_offer", handleWebRTCOffer);
      socket.off("webrtc_answer", handleWebRTCAnswer);
      socket.off("webrtc_ice_candidate", handleWebRTCIce);
    };
  }, [isReceivingCall, isCalling, callActive, localStream, groupId, setCallState, clearCallState, clearOutgoingCallTimeout, startRinging, stopRinging, stopIncomingCallAlert, endCallCleanup, createGroupPeer, initStream, loadGroupCallMembers, updateMemberStatus, showIncomingCallAlert]);

  useEffect(() => {
    return () => {
      stopIncomingCallAlert();
      stopRinging();
    };
  }, [stopIncomingCallAlert, stopRinging]);

  useEffect(() => {
    if (callActive && !localStream) {
      initStream();
    }
  }, [callActive, localStream, initStream]);

  useEffect(() => {
    clearOutgoingCallTimeout();

    if (!isCalling || callActive || groupId) return;

    const displayName = targetUserName || "The person";
    outgoingCallTimeoutRef.current = setTimeout(() => {
      const socket = getSocket();
      if (socket && targetUserId) {
        socket.emit("end_call", { to: targetUserId });
      }

      endCallCleanup(false);
      setMissedCallMessage(`${displayName} did not accept the call.`);
    }, OUTGOING_CALL_TIMEOUT_MS);

    return clearOutgoingCallTimeout;
  }, [
    isCalling,
    callActive,
    groupId,
    targetUserId,
    targetUserName,
    clearOutgoingCallTimeout,
    endCallCleanup,
  ]);

  useEffect(() => {
    if (!callActive || !localStream || !groupId) return;
    
    const socket = getSocket();
    if (!socket) return;
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    
    participants.forEach(async (p) => {
      // If we don't have a peer for this participant yet AND we are the lower ID (initiator)
      if (!peersRef.current[p.userId] && Number(currentUser.id) < Number(p.userId)) {
         console.log(`📡 Auto-negotiating: Initiating offer to ${p.userId}`);
         const peer = createGroupPeer(p.userId, localStream, socket);
         peersRef.current[p.userId] = peer;
         try {
           const offer = await peer.createOffer();
           await peer.setLocalDescription(offer);
           socket.emit("webrtc_offer", { targetUserId: p.userId, signal: offer, groupId });
         } catch (e) {
           console.error("Negotiation error:", e);
         }
      }
    });
  }, [callActive, localStream, participants, groupId, createGroupPeer]);

  const iceCandidateQueue = useRef([]);

  useEffect(() => {
    if (localVideoRef.current && localStream && localVideoRef.current.srcObject !== localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, callActive]);

  useEffect(() => {
    if (callActive && !durationIntervalRef.current) {
      durationIntervalRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (!callActive && durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    };
  }, [callActive]);

  useEffect(() => {
    if (!groupId || !participants.length) return;

    participants.forEach((participant) => {
      updateMemberStatus(participant.userId, "joined", {
        name: participant.name,
        stream: participant.stream,
      });
    });
  }, [groupId, participants, updateMemberStatus]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !callActive) return;

    const handleCallMessage = (message) => {
      const isGroupMessage = groupId && String(message.group_id || message.groupId) === String(groupId);
      const peerId = callerId || targetUserId;
      const isDirectMessage = !groupId && (
        String(message.sender_id || message.senderId) === String(peerId) ||
        String(message.receiver_id || message.receiverId) === String(peerId)
      );

      if (isGroupMessage || isDirectMessage) {
        setCallChatMessages((prev) => [...prev, message]);
      }
    };

    socket.on("receive_message", handleCallMessage);
    return () => socket.off("receive_message", handleCallMessage);
  }, [callActive, callerId, groupId, targetUserId]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getDisplayName = (userLike) => {
    if (!userLike) return "";
    return userLike.name || [userLike.first_name, userLike.last_name].filter(Boolean).join(" ") || "User";
  };

  const getStatusMeta = (status) => {
    if (status === "joined") return { label: "In call", Icon: CheckCircle2, className: "bg-emerald-500/15 text-emerald-300 border-emerald-400/20" };
    if (status === "missed") return { label: "Not picked", Icon: XCircle, className: "bg-red-500/15 text-red-300 border-red-400/20" };
    if (status === "left") return { label: "Left", Icon: PhoneOff, className: "bg-white/10 text-white/55 border-white/10" };
    return { label: "Ringing", Icon: Clock3, className: "bg-amber-500/15 text-amber-200 border-amber-400/20" };
  };

  const getGroupCallTiles = () => {
    const byId = new Map();

    groupCallMembers.forEach((member) => {
      if (member.userId) byId.set(String(member.userId), member);
    });

    participants.forEach((participant) => {
      const key = String(participant.userId);
      byId.set(key, {
        ...(byId.get(key) || {}),
        ...participant,
        userId: participant.userId,
        name: participant.name || byId.get(key)?.name || `User ${participant.userId}`,
        status: "joined",
      });
    });

    if (currentUser?.id && !byId.has(String(currentUser.id))) {
      byId.set(String(currentUser.id), {
        userId: currentUser.id,
        name: "You",
        status: "joined",
        stream: localStream,
        isSelf: true,
      });
    }

    return Array.from(byId.values())
      .map((member) => ({
        ...member,
        isSelf: String(member.userId) === String(currentUser?.id),
        name: String(member.userId) === String(currentUser?.id) ? "You" : member.name,
      }))
      .sort((a, b) => Number(Boolean(b.isSelf)) - Number(Boolean(a.isSelf)) || a.name.localeCompare(b.name));
  };

  const retryGroupMember = (member) => {
    const socket = getSocket();
    if (!socket || !groupId || !member?.userId) return;

    updateMemberStatus(member.userId, "ringing", { stream: null });
    startNoAnswerTimer(member);
    socket.emit("retry_group_call", {
      groupId,
      targetUserId: member.userId,
      isVideo,
    });
    socket.emit("start_group_call", {
      groupId,
      targetUserId: member.userId,
      isVideo,
    });
  };

  const handleCallChatSend = (message, type = "text") => {
    if (!message) return;
    const socket = getSocket();
    const payload = {
      message,
      type,
      clientTime: new Date(),
    };

    if (groupId) payload.groupId = groupId;
    else payload.receiverId = callerId || targetUserId;

    setCallChatMessages((prev) => [
      ...prev,
      {
        ...payload,
        sender_id: currentUser?.id,
        sender_name: getDisplayName(currentUser) || "You",
      },
    ]);
    socket?.emit("send_message", payload);
  };

  const initiateCallFlow = useCallback(async () => {
    const socket = getSocket();
    if (!socket) return;
    try {
      const stream = await initStream();
      if (!stream) {
        clearCallState();
        return;
      }

      if (groupId) {
        console.log("📞 Initiating Group Call:", groupId);
        await loadGroupCallMembers(groupId, true);
        socket.emit("start_group_call", { groupId, isVideo });
        socket.emit("join_group_call", { groupId });
        setCallState({ callActive: true, isCalling: false });
      } else {
        console.log("📞 Initiating 1-on-1 Call to:", targetUserId);
        const peer = createPeer(stream, socket, targetUserId);
        connectionRef.current = peer;
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        socket.emit("call_user", {
          userToCall: targetUserId,
          signalData: offer,
          from: user.id,
          name: `${user.first_name} ${user.last_name || ""}`.trim(),
          isVideo: isVideo,
        });
      }
    } catch (err) {
      console.error("Initiate error:", err);
      endCallCleanup();
    }
  }, [
    clearCallState,
    createPeer,
    endCallCleanup,
    groupId,
    initStream,
    isVideo,
    loadGroupCallMembers,
    setCallState,
    targetUserId,
  ]);

  useEffect(() => {
    if (isCalling && !callActive && !callInitiatedRef.current) {
      callInitiatedRef.current = true;
      initiateCallFlow();
    }
  }, [isCalling, callActive, initiateCallFlow]);

  // ==========================================
  // 🎮 ACTIONS
  // ==========================================

  const answerCall = async () => {
    stopIncomingCallAlert();
    stopRinging();
    const stream = await initStream();
    if (!stream) return;
    
    const socket = getSocket();
    if (groupId) {
      await loadGroupCallMembers(groupId, false);
      setCallState({ callActive: true, isReceivingCall: false });
      updateMemberStatus(currentUser.id, "joined", { stream });
      socket.emit("join_group_call", { groupId });
    } else {
      setCallState({ callActive: true, isReceivingCall: false });
      const peer = createPeer(stream, socket, callerId);
      connectionRef.current = peer;
      try {
        await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        socket.emit("make_answer", { to: callerId, signal: answer });
      } catch (e) {
        console.error("Answer error:", e);
        endCallCleanup();
      }
    }
  };

  const rejectCall = () => {
    stopIncomingCallAlert();
    stopRinging();
    const socket = getSocket();
    if (socket) {
      if (groupId) socket.emit("reject_group_call", { groupId, to: callerId });
      else socket.emit("reject_call", { to: callerId });
    }
    clearCallState();
  };

  const endCall = () => {
    const socket = getSocket();
    const target = callerId || targetUserId;
    console.log("☎️ Ending call, notifying:", target);
    if (socket) {
      if (groupId) {
        socket.emit("leave_group_call", { groupId });
        socket.emit("end_group_call", { groupId });
        socket.emit("end_call", { groupId });
      } else if (target) {
        socket.emit("end_call", { to: target });
      }
    }
    endCallCleanup(true); // Always show modal when we end the call locally
  };

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
        setIsMuted(!track.enabled);
      });
    }
  };

  const toggleCamera = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
        setIsCameraOff(!track.enabled);
      });
    }
  };

  const toggleSpeaker = () => {
    setIsSpeakerOn(!isSpeakerOn);
    // In a browser, speaker toggling is usually handled by the system, 
    // but we can simulate it for UI purposes.
  };

  if (!isCalling && !isReceivingCall && !callActive && !isBusyResponse && !missedCallMessage) return null;

  return (
    <div
      ref={callRootRef}
      className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#0b1014] text-white'}`}
    >
      
      {/* ⚠️ --- BUSY RESPONSE MODAL --- */}
      {isBusyResponse && (
        <div className="fixed inset-0 z-[2000001] bg-black/60 flex items-center justify-center p-6 backdrop-blur-sm">
          <div className="bg-[#232d36] rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-white/10 animate-in zoom-in-95">
             <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <PhoneOff className="w-10 h-10 text-red-500" />
             </div>
             <h3 className="text-2xl font-bold text-center mb-2">User Busy</h3>
             <p className="text-gray-400 text-center mb-8">The person you are trying to call is currently on another call.</p>
             <button onClick={() => setIsBusyResponse(false)} className="w-full py-4 bg-[#00a884] hover:bg-[#008f72] rounded-full font-bold transition-colors">
               Dismiss
             </button>
          </div>
        </div>
      )}

      {/* ⏱️ --- NO ANSWER MODAL --- */}
      {missedCallMessage && (
        <div className="fixed inset-0 z-[2000001] bg-black/60 flex items-center justify-center p-6 backdrop-blur-sm">
          <div className="bg-[#232d36] rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-white/10 animate-in zoom-in-95">
             <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <PhoneOff className="w-10 h-10 text-amber-400" />
             </div>
             <h3 className="text-2xl font-bold text-center mb-2">No Answer</h3>
             <p className="text-gray-400 text-center mb-8">{missedCallMessage}</p>
             <button
               onClick={() => setMissedCallMessage("")}
               className="w-full py-4 bg-[#00a884] hover:bg-[#008f72] rounded-full font-bold transition-colors"
             >
               Dismiss
             </button>
          </div>
        </div>
      )}

      {/* 📞 --- CALL ENDED MODAL --- */}
      {showCallEndedModal && (
        <div className="fixed inset-0 z-[2000001] bg-black/60 flex items-center justify-center p-6 backdrop-blur-sm">
          <div className="bg-[#232d36] rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-white/10 animate-in zoom-in-95">
             <div className="w-20 h-20 bg-gray-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <PhoneOff className="w-10 h-10 text-gray-400" />
             </div>
             <h3 className="text-2xl font-bold text-center mb-2">Call Ended</h3>
             <p className="text-gray-400 text-center mb-8">The call has ended.</p>
             <button onClick={() => setShowCallEndedModal(false)} className="w-full py-4 bg-gray-600 hover:bg-gray-700 rounded-full font-bold transition-colors">
               Dismiss
             </button>
          </div>
        </div>
      )}

      {/* Incoming call modal */}
      {isReceivingCall && !callActive && (
        <div className="w-full h-full bg-[#0b1014] flex flex-col items-center justify-between py-20 px-10 animate-in fade-in duration-500">
           <div className="flex flex-col items-center gap-4">
              <div className="w-32 h-32 bg-[#232d36] rounded-full flex items-center justify-center overflow-hidden border-4 border-[#00a884]">
                 {groupId ? <Users className="w-16 h-16 text-white" /> : <div className="text-6xl font-bold">{(callerName?.charAt(0).toUpperCase() || "U")}</div>}
              </div>
              <h2 className="text-4xl font-semibold">{groupId ? groupName : callerName}</h2>
              <p className="text-[#00a884] font-medium tracking-wide uppercase text-sm">{isVideo ? "Incoming video call" : "Incoming voice call"}</p>
           </div>
           
           <div className="flex flex-col items-center gap-10">
              {autoplayBlocked && (
                <button onClick={() => { ringtoneRef.current?.play(); setAutoplayBlocked(false); }} className="text-[#00a884] text-xs underline animate-pulse">
                   Click to enable ringtone
                </button>
              )}
              <div className="flex gap-20">
                <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    <PhoneOff className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-400">Decline</span>
                </button>
                <button onClick={answerCall} className="group flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-[#00a884] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    {isVideo ? <Video className="w-7 h-7 text-white" /> : <Phone className="w-7 h-7 text-white" />}
                  </div>
                  <span className="text-sm font-medium text-gray-400">Accept</span>
                </button>
              </div>
           </div>
        </div>
      )}

      {/* Outgoing call modal */}
      {isCalling && !callActive && (
        <div className="w-full h-full bg-[#0b1014] flex flex-col items-center justify-between py-20 px-10 animate-in fade-in duration-500">
           <div className="flex flex-col items-center gap-4">
              <div className="w-32 h-32 bg-[#232d36] rounded-full flex items-center justify-center overflow-hidden">
                {groupId ? <Users className="w-16 h-16 text-white" /> : <div className="text-6xl font-bold">{(targetUserName?.charAt(0).toUpperCase() || "U")}</div>}
              </div>
              <h2 className="text-4xl font-semibold">{groupId ? groupName : targetUserName}</h2>
              <p className="text-gray-400 font-medium tracking-wide">CALLING</p>
           </div>
           
           <div className="flex flex-col items-center gap-20">
              <div className="flex flex-col items-center gap-2 text-gray-500">
                 <ShieldCheck className="w-5 h-5" />
                 <span className="text-[10px] uppercase tracking-widest font-bold">End-to-end encrypted</span>
              </div>
              <button onClick={endCall} className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg hover:rotate-90 transition-all duration-500">
                <PhoneOff className="w-7 h-7 text-white" />
              </button>
           </div>
        </div>
      )}

      {/* Active call screen */}
      {callActive && (
        <div className={`w-full h-full flex flex-col ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-3xl overflow-hidden shadow-2xl border border-white/10' : 'bg-[#0b1014]'}`}>
          {!groupId && (
            <audio 
              ref={(el) => {
                if (el && remoteStream && el.srcObject !== remoteStream) {
                  el.srcObject = remoteStream;
                }
              }}
              autoPlay 
            />
          )}
          
          {/* Header Info */}
          {!isMinimized && (
            <div className="p-6 flex items-center justify-between absolute top-0 left-0 w-full z-20 bg-gradient-to-b from-black/60 to-transparent">
               <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#232d36] rounded-full flex items-center justify-center overflow-hidden">
                     {groupId ? <Users className="w-5 h-5" /> : <div className="text-sm font-bold">{(callerName || targetUserName)?.charAt(0)}</div>}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{groupId ? groupName : (callerName || targetUserName)}</h3>
                    <p className="text-[10px] text-gray-400">
                      {formatDuration(callDuration)} • End-to-end encrypted
                    </p>
                  </div>
               </div>
               <button onClick={() => setIsMinimized(true)} className="p-2 hover:bg-white/10 rounded-full">
                  <Minimize2 className="w-5 h-5" />
                </button>
             </div>
           )}

           {/* Main Display Area */}
          <div className="flex-1 relative overflow-hidden">
            
            {/* Audio Mode: Large Avatar */}
            {!groupId && !isVideo && participants.length === 0 && (
               <div className="w-full h-full flex flex-col items-center justify-center gap-8 bg-[#0b1014]">
                  <div className="w-48 h-48 bg-[#232d36] rounded-full flex items-center justify-center overflow-hidden shadow-2xl border-4 border-white/5">
                     <div className="text-8xl font-bold">{(callerName || targetUserName)?.charAt(0)}</div>
                  </div>
                  <div className="flex items-center gap-2 text-[#00a884]">
                     <div className="w-2 h-2 bg-[#00a884] rounded-full animate-pulse"></div>
                     <span className="text-sm font-semibold uppercase tracking-widest">In Call</span>
                  </div>
               </div>
            )}

            {/* Video Mode or Group Grid */}
            {(isVideo || participants.length > 0 || groupId) && (
              <div className="w-full h-full relative">
                
                {/* 1-on-1 Remote Video */}
                {!groupId && isVideo && remoteStream && (
                  <div className="absolute inset-0 w-full h-full bg-black">
                    <video 
                      ref={(el) => {
                        if (el && remoteStream && el.srcObject !== remoteStream) {
                          el.srcObject = remoteStream;
                        }
                      }}
                      className="w-full h-full object-cover" 
                      autoPlay 
                      playsInline 
                    />
                    <div className="absolute top-24 left-6 bg-black/40 px-3 py-1 rounded-lg text-xs font-medium backdrop-blur-sm z-10">
                      {callerName || targetUserName}
                    </div>
                  </div>
                )}

                {/* 1-on-1 Local Video (Floating) */}
                {!groupId && isVideo && !isMinimized && (
                   <div className="absolute top-24 right-6 w-32 h-44 bg-[#1e2428] rounded-2xl overflow-hidden border border-white/10 shadow-xl z-30 group cursor-move">
                      <video ref={localVideoRef} className="w-full h-full object-cover" autoPlay playsInline muted />
                      <div className="absolute bottom-2 left-2 bg-black/40 px-2 py-0.5 rounded text-[8px] font-bold uppercase">You</div>
                   </div>
                )}

                {/* Group Participant Grid */}
                {groupId && (
                  <div className="h-full w-full overflow-y-auto px-5 pb-32 pt-24">
                    <div className="mb-5 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/45">Group call</p>
                        <h4 className="text-lg font-semibold text-white">{groupName || "Team call"}</h4>
                      </div>
                      <div className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-xs text-white/65">
                        {getGroupCallTiles().filter(member => member.status === "joined").length} joined
                      </div>
                    </div>

                    <div className={`grid gap-4 ${
                      getGroupCallTiles().length <= 2 ? "grid-cols-1 lg:grid-cols-2" :
                      getGroupCallTiles().length <= 4 ? "grid-cols-1 md:grid-cols-2" :
                      "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
                    }`}>
                      {getGroupCallTiles().map((member) => {
                        const status = getStatusMeta(member.status);
                        const StatusIcon = status.Icon;
                        const stream = member.isSelf ? localStream : member.stream;

                        return (
                          <div
                            key={member.userId}
                            className={`relative min-h-[260px] overflow-hidden rounded-2xl border bg-[#111820] shadow-2xl shadow-black/25 ${
                              member.isSelf ? "border-cyan-400/35" :
                              member.status === "missed" ? "border-red-400/25" :
                              member.status === "ringing" ? "border-amber-400/25" :
                              "border-white/10"
                            }`}
                          >
                            {stream && isVideo && member.status === "joined" ? (
                              <video
                                className="h-full min-h-[260px] w-full bg-black object-cover"
                                autoPlay
                                playsInline
                                muted={member.isSelf}
                                ref={el => {
                                  if (el && stream && el.srcObject !== stream) el.srcObject = stream;
                                }}
                              />
                            ) : (
                              <div className="flex h-full min-h-[260px] flex-col items-center justify-center gap-4 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.20),transparent_45%),linear-gradient(135deg,#111820,#17212b)] p-5">
                                <div className={`flex h-20 w-20 items-center justify-center rounded-2xl text-2xl font-bold shadow-lg ${
                                  member.status === "missed" ? "bg-red-500/15 text-red-200" :
                                  member.status === "ringing" ? "bg-amber-500/15 text-amber-100" :
                                  member.isSelf ? "bg-cyan-500/15 text-cyan-100" :
                                  "bg-white/10 text-white"
                                }`}>
                                  {member.isSelf ? "You" : member.name?.charAt(0)?.toUpperCase() || "U"}
                                </div>

                                {member.status === "ringing" && (
                                  <div className="flex h-7 items-end gap-1">
                                    {[2, 5, 8, 5, 2].map((height, index) => (
                                      <span
                                        key={index}
                                        className="w-1 rounded-full bg-amber-300/90 animate-pulse"
                                        style={{ height: `${height * 3}px`, animationDelay: `${index * 0.12}s` }}
                                      />
                                    ))}
                                  </div>
                                )}

                                {member.status === "missed" && (
                                  <p className="max-w-[80%] text-center text-sm font-semibold text-red-100">
                                    {member.name} not picked
                                  </p>
                                )}
                              </div>
                            )}

                            {stream && !isVideo && member.status === "joined" && !member.isSelf && (
                              <audio autoPlay ref={el => { if (el && stream && el.srcObject !== stream) el.srcObject = stream; }} />
                            )}

                            <div className="absolute left-4 right-4 top-4 flex items-start justify-between gap-3">
                              <div className="min-w-0 rounded-xl border border-white/10 bg-black/35 px-3 py-2 backdrop-blur-md">
                                <p className="truncate text-sm font-semibold text-white">{member.name}</p>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">{member.isSelf ? "You" : "Member"}</p>
                              </div>
                              <div className={`flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${status.className}`}>
                                <StatusIcon className="h-3.5 w-3.5" />
                                {status.label}
                              </div>
                            </div>

                            {member.status === "missed" && isAdminUser && !member.isSelf && (
                              <button
                                onClick={() => retryGroupMember(member)}
                                className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-semibold text-white backdrop-blur-md transition hover:bg-white/20"
                                type="button"
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                                Retry
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {callChatOpen && !isMinimized && (
            <div className="absolute bottom-28 right-5 top-24 z-50 flex w-[min(390px,calc(100vw-40px))] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#151923]/95 shadow-2xl shadow-black/45 backdrop-blur-2xl">
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                <div>
                  <h4 className="font-semibold text-white">Call chat</h4>
                  <p className="text-xs text-white/45">Messages and attachments during this call</p>
                </div>
                <button
                  onClick={() => setCallChatOpen(false)}
                  className="rounded-xl p-2 text-white/55 transition hover:bg-white/10 hover:text-white"
                  type="button"
                  title="Close chat"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto p-4">
                {callChatMessages.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center text-white/45">
                    <MessageCircle className="mb-3 h-9 w-9 text-white/25" />
                    <p className="text-sm font-medium text-white/65">No call messages yet</p>
                    <p className="mt-1 text-xs">Send text or attachments without leaving the call.</p>
                  </div>
                ) : (
                  callChatMessages.map((message, index) => {
                    const isMine = String(message.sender_id || message.senderId) === String(currentUser?.id);
                    return (
                      <div key={`${message.clientTime || message.created_at || index}`} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${
                          isMine ? "bg-violet-600 text-white" : "bg-white/10 text-white/85"
                        }`}>
                          {!isMine && <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-white/45">{message.sender_name || message.senderName || "Member"}</p>}
                          {message.type === "image" ? (
                            <button
                              type="button"
                              onClick={() => setCallAttachmentPreview({ type: "image", url: getCallAttachmentUrl(message.message), name: getCallAttachmentName(message.message) })}
                              className="overflow-hidden rounded-xl"
                            >
                              <img src={getCallAttachmentUrl(message.message)} alt="Attachment" className="max-h-40 rounded-xl object-cover transition-opacity hover:opacity-90" />
                            </button>
                          ) : message.type === "file" ? (
                            <button
                              type="button"
                              onClick={() => setCallAttachmentPreview({ type: "file", url: getCallAttachmentUrl(message.message), name: getCallAttachmentName(message.message) })}
                              className="flex items-center gap-2 rounded-xl bg-black/20 px-3 py-2 text-left text-white/85 transition hover:bg-black/30"
                            >
                              <FileIcon className="h-4 w-4 text-violet-200" />
                              <span className="max-w-[180px] truncate">{getCallAttachmentName(message.message)}</span>
                            </button>
                          ) : (
                            <p>{message.message}</p>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <ChatInput onSend={handleCallChatSend} members={groupCallMembers} />
            </div>
          )}

          {callAttachmentPreview && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
              <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-white/15 bg-[#11101f] shadow-2xl">
                <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{callAttachmentPreview.name}</p>
                    <p className="text-xs text-white/45">Attachment preview</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={callAttachmentPreview.url}
                      download={callAttachmentPreview.name}
                      className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/15"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </a>
                    <button
                      type="button"
                      onClick={() => setCallAttachmentPreview(null)}
                      className="rounded-xl bg-white/10 p-2 text-white transition hover:bg-white/15"
                    >
                      <XCircle className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="flex min-h-[320px] flex-1 items-center justify-center overflow-auto bg-black/25 p-4">
                  {callAttachmentPreview.type === "image" ? (
                    <img src={callAttachmentPreview.url} alt={callAttachmentPreview.name} className="max-h-[78vh] max-w-full rounded-2xl object-contain" />
                  ) : isCallPreviewableFile(callAttachmentPreview.name) ? (
                    <iframe title={callAttachmentPreview.name} src={callAttachmentPreview.url} className="h-[78vh] w-full rounded-2xl border border-white/10 bg-white" />
                  ) : (
                    <div className="max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
                      <FileIcon className="mx-auto mb-4 h-12 w-12 text-violet-200" />
                      <p className="text-sm font-semibold text-white">Preview not available</p>
                      <p className="mt-2 text-xs text-white/50">Use Download to save this attachment.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Control Bar */}
          {!isMinimized && (
            <div className="absolute bottom-0 left-0 w-full p-8 pb-12 flex items-center justify-center gap-6 z-40 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
               <button onClick={toggleSpeaker} title="Speaker" className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isSpeakerOn ? 'bg-white/10 hover:bg-white/20' : 'bg-white text-black'}`}>
                 {isSpeakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
               </button>
               <button onClick={() => setCallChatOpen((open) => !open)} title="Call chat" className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${callChatOpen ? 'bg-violet-600 text-white' : 'bg-white/10 hover:bg-white/20'}`}>
                 <MessageCircle className="w-6 h-6" />
               </button>
               {isVideo && (
                 <button onClick={toggleCamera} title="Camera" className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isCameraOff ? 'bg-red-500 text-white' : 'bg-white/10 hover:bg-white/20'}`}>
                   {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
                 </button>
               )}
               <button onClick={toggleMute} title="Mute" className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isMuted ? 'bg-red-500 text-white' : 'bg-white/10 hover:bg-white/20'}`}>
                 {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
               </button>
               <button onClick={endCall} title="End Call" className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-xl hover:rotate-90 hover:bg-red-700 transition-all duration-500">
                 <PhoneOff className="w-8 h-8 text-white" />
               </button>
            </div>
          )}

          {isMinimized && (
             <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
                <Maximize2 className="text-white w-12 h-12" />
             </button>
          )}
        </div>
      )}
    </div>
  );
}











// import { useEffect, useRef, useState, useCallback } from "react";
// import { useCallStore } from "../app/callStore";
// import { getSocket } from "../socket/socket";
// import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2 } from "lucide-react";
// import ringtoneSound from "../assets/VibrateCall.mp3";
// import toast from "react-hot-toast";
// export default function CallManager() {
//   const {
//     isReceivingCall,
//     isCalling,
//     callActive,
//     callerName,
//     callerSignal,
//     callerId,
//     targetUserId,
//     targetUserName,
//     isVideo,
//     setCallState,
//     clearCallState,
//   } = useCallStore();


//   const remoteAudioRef = useRef(null);
//   const [localStream, setLocalStream] = useState(null);
//   const [remoteStream, setRemoteStream] = useState(null);

//   const [isMuted, setIsMuted] = useState(false);
//   const [isCameraOff, setIsCameraOff] = useState(false);
//   const [isMinimized, setIsMinimized] = useState(false);
//   const [autoplayBlocked, setAutoplayBlocked] = useState(false);

//   const localVideoRef = useRef(null);
//   const remoteVideoRef = useRef(null);
//   const connectionRef = useRef(null);
//   const ringtoneRef = useRef(null);
//   const vibrateInterval = useRef(null);
//   const callInitiatedRef = useRef(false);
//   const iceCandidateQueue = useRef([]);

//   // ==========================================
//   // 🎵 HELPERS (useCallback to avoid hoisting errors)
//   // ==========================================

//   const startRinging = useCallback(() => {
//     if (!ringtoneRef.current) {
//       ringtoneRef.current = new Audio(ringtoneSound);
//       ringtoneRef.current.loop = true;
//     }
//     ringtoneRef.current.play().then(() => {
//       setAutoplayBlocked(false);
//     }).catch(e => {
//       console.log("Audio block:", e);
//       setAutoplayBlocked(true);
//     });
//     if (navigator.vibrate) {
//       vibrateInterval.current = setInterval(() => {
//         navigator.vibrate([400, 200, 400]);
//       }, 1200);
//     }
//   }, []);

//   const stopRinging = useCallback(() => {
//     if (ringtoneRef.current) {
//       ringtoneRef.current.pause();
//       ringtoneRef.current.currentTime = 0;
//     }
//     if (vibrateInterval.current) clearInterval(vibrateInterval.current);
//     if (navigator.vibrate) navigator.vibrate(0);
//   }, []);

//   // const initStream = useCallback(async (videoRequired) => {
//   //   try {
//   //     const stream = await navigator.mediaDevices.getUserMedia({
//   //       video: videoRequired ? { width: 1280, height: 720, facingMode: "user" } : false,
//   //       audio: true
//   //     });
//   //     setLocalStream(stream);
//   //     return stream;
//   //   } catch (err) {
//   //     console.error("Stream access denied:", err);
//   //     return null;
//   //   }
//   // }, []);

//   const initStream = useCallback(async (videoRequired = true) => {
//     try {
//       const stream = await navigator.mediaDevices.getUserMedia({
//         video: videoRequired ? { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" } : false,
//         audio: true
//       });
  
//       console.log("🎥 Stream acquired:", stream.id, "tracks:", stream.getTracks().length);

//       // Ensure tracks are enabled
//       stream.getTracks().forEach(track => {
//         track.enabled = true;
//       });

//       setLocalStream(stream);
  
//       if (localVideoRef.current) {
//         localVideoRef.current.srcObject = stream;
//         localVideoRef.current.play().catch(e => console.log("Local play error:", e));
//       }
  
//       return stream;
//     } catch (err) {
//       console.error("Stream access denied:", err);
//       toast.error("Could not access camera/microphone");
//       return null;
//     }
//   }, []);


//   const createPeer = useCallback((stream, socket, toUserId) => {
//     const peer = new RTCPeerConnection({
//       iceServers: [
//         { urls: "stun:stun.l.google.com:19302" },
//         { urls: "stun:stun1.l.google.com:19302" },
//         { urls: "stun:stun2.l.google.com:19302" },
//       ]
//     });
  
//     // ✅ Add tracks
//     if (stream) {
//       stream.getTracks().forEach(track => {
//         peer.addTrack(track, stream);
//       });
//     }
  
//     // ✅ Handle remote stream properly
//     peer.ontrack = (event) => {
//       console.log("🎬 Remote track received:", event.track.kind);
  
//       if (event.streams && event.streams[0]) {
//         setRemoteStream(event.streams[0]);
//       } else {
//         // Fallback for browsers that don't provide event.streams
//         setRemoteStream(prev => {
//           if (prev) {
//             prev.addTrack(event.track);
//             return new MediaStream(prev.getTracks());
//           } else {
//             return new MediaStream([event.track]);
//           }
//         });
//       }
//     };
  
//     // ✅ ICE
//     peer.onicecandidate = (event) => {
//       if (event.candidate) {
//         socket.emit("ice_candidate", {
//           to: toUserId,
//           candidate: event.candidate
//         });
//       }
//     };
  
//     return peer;
//   }, []);




//   const endCallCleanup = useCallback(() => {
//     // 📝 LOG CALL IN CHAT BEFORE CLEANUP
//     if (callActive || isCalling) {
//       const socket = getSocket();
//       const targetId = callerId || targetUserId;
//       const groupId = useCallStore.getState().groupId;
//       if (socket && (targetId || groupId)) {
//         socket.emit("send_message", {
//           receiverId: targetId || null,
//           groupId: groupId || null,
//           message: `📞 ${isVideo ? 'Video' : 'Audio'} call ended`,
//           type: "call_log", // Custom type for UI styling
//           clientTime: new Date(),
//         });
//       }
//     }

//     // if (localStream) localStream.getTracks().forEach(track => track.stop());
//     if (localStream) {
//       localStream.getTracks().forEach(track => track.stop());
//     }
    
//     if (remoteStream) {
//       remoteStream.getTracks().forEach(track => track.stop());
//     }
//     if (connectionRef.current) {
//       connectionRef.current.close();
//       connectionRef.current = null;
//     }
//     setLocalStream(null);
//     setRemoteStream(null);
//     iceCandidateQueue.current = [];
//     clearCallState();
//     stopRinging();
//     callInitiatedRef.current = false;
//   }, [callActive, isCalling, callerId, targetUserId, isVideo, localStream, clearCallState, stopRinging]);

//   const initiateCallFlow = useCallback(async () => {
//     const socket = getSocket();
//     if (!socket) return;
//     try {
//       const stream = await initStream(isVideo);
//       if (!stream) {
//         clearCallState();
//         return;
//       }
//       const peer = createPeer(stream, socket, targetUserId);
//       connectionRef.current = peer;
//       const offer = await peer.createOffer();
//       await peer.setLocalDescription(offer);
//       const user = JSON.parse(localStorage.getItem("user") || "{}");
//       socket.emit("call_user", {
//         userToCall: targetUserId || null,
//         groupToCall: useCallStore.getState().groupId || null,
//         signalData: offer,
//         from: user.id,
//         name: `${user.first_name} ${user.last_name || ""}`,
//         isVideo: isVideo,
//       });
//     } catch (err) {
//       console.error("Initiate error:", err);
//       endCallCleanup();
//     }
//   }, [isVideo, targetUserId, clearCallState, initStream, createPeer, endCallCleanup]);

//   // ==========================================
//   // 🧨 EFFECTS
//   // ==========================================

//   useEffect(() => {
//     const socket = getSocket();
//     if (!socket) return;

//     const handleIncomingCall = (data) => {
//       console.log("☎️ Incoming call from:", data.from);
//       setCallState({
//         isReceivingCall: true,
//         callerId: data.from,
//         callerName: data.groupId ? `Group: ${data.name}` : data.name,
//         callerSignal: data.signal,
//         isVideo: data.isVideo,
//         groupId: data.groupId || null,
//       });
//       startRinging();
//     };

//     const handleCallAccepted = async (signal) => {
//       console.log("✅ Call accepted! Establishing connection...");
//       stopRinging();
//       setCallState({ callActive: true, isCalling: false });
//       if (connectionRef.current) {
//         try {
//           await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
//           // Process queued ICE candidates
//           while (iceCandidateQueue.current.length > 0) {
//             const candidate = iceCandidateQueue.current.shift();
//             await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
//           }
//         } catch (e) {
//           console.error("Error setting remote description:", e);
//         }
//       }
//     };

//     const handleCallRejected = () => {
//       stopRinging();
//       endCallCleanup();
//     };

//     const handleCallEnded = () => {
//       stopRinging();
//       endCallCleanup();
//     };

//     const handleIceCandidate = async (candidate) => {
//       if (connectionRef.current && connectionRef.current.remoteDescription) {
//         try {
//           await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
//         } catch (e) {
//           console.error("Error adding ice candidate:", e);
//         }
//       } else {
//         iceCandidateQueue.current.push(candidate);
//       }
//     };

//     socket.on("incoming_call", handleIncomingCall);
//     socket.on("call_accepted", handleCallAccepted);
//     socket.on("call_rejected", handleCallRejected);
//     socket.on("call_ended", handleCallEnded);
//     socket.on("ice_candidate", handleIceCandidate);

//     return () => {
//       socket.off("incoming_call", handleIncomingCall);
//       socket.off("call_accepted", handleCallAccepted);
//       socket.off("call_rejected", handleCallRejected);
//       socket.off("call_ended", handleCallEnded);
//       socket.off("ice_candidate", handleIceCandidate);
//     };
//   }, [setCallState, stopRinging, startRinging, endCallCleanup]);

//   useEffect(() => {
//     if (isCalling && !callActive && !callInitiatedRef.current) {
//       callInitiatedRef.current = true;
//       initiateCallFlow();
//     } else if (!isCalling && !callActive) {
//       callInitiatedRef.current = false;
//     }
//   }, [isCalling, callActive, initiateCallFlow]);



 
//   // useEffect(() => {
//   //   if (localVideoRef.current && localStream) {
//   //     localVideoRef.current.srcObject = localStream;
  
//   //     const playPromise = localVideoRef.current.play();
//   //     if (playPromise !== undefined) {
//   //       playPromise.catch((err) => {
//   //         console.log("❌ Local video autoplay blocked:", err);
//   //       });
//   //     }
//   //   }
//   // }, [localStream]);

//   useEffect(() => {
//     if (localVideoRef.current && localStream) {



//           // 🔥 ADD HERE
//     console.log(
//       "LOCAL VIDEO TRACK ENABLED:",
//       localStream?.getVideoTracks()[0]?.enabled
//     );

//       console.log("✅ Attaching LOCAL stream");
  
//       localVideoRef.current.srcObject = localStream;
//       localVideoRef.current.muted = true; // 🔥 important
//       localVideoRef.current.playsInline = true;
  
//       const playPromise = localVideoRef.current.play();
//       if (playPromise !== undefined) {
//         playPromise.catch((err) => {
//           console.log("❌ Local video autoplay blocked:", err);
//         });
//       }
//     }
//   }, [localStream, callActive, isMinimized]);


//   useEffect(() => {
//     if (!remoteStream || !callActive) return;
  
//     console.log("🔊 Syncing remote stream to elements...");
  
//     const attachStream = () => {
//       // 🎥 VIDEO
//       if (remoteVideoRef.current) {
//         if (remoteVideoRef.current.srcObject !== remoteStream) {
//           console.log("✅ Attaching remote stream to VIDEO");
//           remoteVideoRef.current.srcObject = remoteStream;
//         }
//         remoteVideoRef.current.play().catch(e => console.log("Remote video play error:", e));
//       }
  
//       // 🔊 AUDIO
//       if (remoteAudioRef.current) {
//         if (remoteAudioRef.current.srcObject !== remoteStream) {
//           console.log("✅ Attaching remote stream to AUDIO");
//           remoteAudioRef.current.srcObject = remoteStream;
//         }
//         remoteAudioRef.current.play().catch(e => console.log("Remote audio play error:", e));
//       }
//     };

//     // Small delay to ensure refs are populated after re-render
//     const timeoutId = setTimeout(attachStream, 100);
//     return () => clearTimeout(timeoutId);
  
//   }, [remoteStream, callActive, isVideo, isMinimized]);



//   // ==========================================
//   // 🎮 ACTIONS
//   // ==========================================

//   const answerCall = async () => {
//     stopRinging();
//     const stream = await initStream(isVideo);
//     if (!stream) return;
//     setCallState({ callActive: true, isReceivingCall: false });
//     const socket = getSocket();
//     const peer = createPeer(stream, socket, callerId);
//     connectionRef.current = peer;
//     try {
//       await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
//       const answer = await peer.createAnswer();
//       await peer.setLocalDescription(answer);
//       socket.emit("make_answer", { to: callerId, signal: answer });

//       // Process queued candidates after remote description is set
//       while (iceCandidateQueue.current.length > 0) {
//         const candidate = iceCandidateQueue.current.shift();
//         await peer.addIceCandidate(new RTCIceCandidate(candidate));
//       }
//     } catch (e) {
//       console.error("Answer error:", e);
//       endCallCleanup();
//     }
//   };

//   const rejectCall = () => {
//     stopRinging();
//     const socket = getSocket();
//     if (socket) socket.emit("reject_call", { to: callerId });
//     clearCallState();
//   };

//   const endCall = () => {
//     const socket = getSocket();
//     const targetId = callerId || targetUserId;
//     const groupId = useCallStore.getState().groupId;

//     if (socket) {
//       if (targetId) socket.emit("end_call", { to: targetId });
//       else if (groupId) socket.emit("end_call", { groupId });
//     }
//     endCallCleanup();
//   };

//   const toggleMute = () => {
//     if (localStream) {
//       localStream.getAudioTracks().forEach(track => {
//         track.enabled = !track.enabled;

//         console.log("VIDEO TRACK ENABLED:", track.enabled);
//         setIsMuted(!track.enabled);
//       });
//     }
//   };

//   // const toggleCamera = () => {
//   //   if (localStream) {
//   //     console.log("LOCAL STREAM TRACKS:", localStream.getVideoTracks());

//   //     localStream.getVideoTracks().forEach(track => {
//   //       track.enabled = !track.enabled;
//   //       setIsCameraOff(!track.enabled);
//   //     });
//   //   }
//   // };




//   const toggleCamera = () => {
//     if (!localStream) return;
  
//     const videoTrack = localStream.getVideoTracks()[0];
//     if (!videoTrack) return;
  
//     const newState = !videoTrack.enabled;
//     videoTrack.enabled = newState;
  
//     console.log("🎥 Camera enabled:", newState);
  
//     setIsCameraOff(!newState); // UI follows real state
//   };

//   if (!isCalling && !isReceivingCall && !callActive) return null;

//   return (
//     <div className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#050508]/70 backdrop-blur-[32px]'}`}>

//       {/* 🔮 --- INCOMING CALL MODAL --- */}
//       {isReceivingCall && !callActive && (
//         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in zoom-in-95 duration-500">
//           <div className="relative">
//             <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
//             <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-indigo-600 via-purple-500 to-pink-500 animate-spin-slow">
//               <div className="w-full h-full bg-[#12141c] rounded-full flex justify-center items-center text-6xl text-white font-black border-2 border-[#12141c]">
//                 {callerName?.charAt(0).toUpperCase() || "U"}
//               </div>
//             </div>
//             <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-emerald-500 rounded-full border-4 border-[#12141c] flex items-center justify-center animate-bounce shadow-xl shadow-emerald-500/20">
//               <Phone className="text-white w-5 h-5 fill-white" />
//             </div>
//           </div>
//           <div className="text-center">
//             <h2 className="text-4xl font-black text-white tracking-tight mb-3">{callerName || "Unknown"}</h2>
//             {autoplayBlocked && (
//               <button
//                 onClick={() => {
//                   ringtoneRef.current?.play();
//                   setAutoplayBlocked(false);
//                 }}
//                 className="mb-4 text-[10px] text-indigo-400 font-bold uppercase animate-pulse underline"
//               >
//                 Click to enable ringtone
//               </button>
//             )}
//             <p className="text-purple-400 font-black uppercase tracking-[0.2em] text-[10px] bg-purple-500/10 px-4 py-1.5 rounded-full border border-purple-500/20">
//               {isVideo ? "Incoming Video Request" : "Incoming Audio Request"}
//             </p>
//           </div>
//           <div className="flex gap-12 mt-2">
//             <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
//               <div className="w-16 h-16 bg-red-500/10 hover:bg-red-500 rounded-2xl flex items-center justify-center text-red-500 hover:text-white transition-all duration-300 border border-red-500/20 shadow-lg shadow-red-500/10">
//                 <PhoneOff className="w-7 h-7" />
//               </div>
//               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Decline</span>
//             </button>
//             <button onClick={answerCall} className="group flex flex-col items-center gap-3">
//               <div className="w-16 h-16 bg-emerald-500/10 hover:bg-emerald-500 rounded-2xl flex items-center justify-center text-emerald-500 hover:text-white transition-all duration-300 border border-emerald-500/20 shadow-lg shadow-emerald-500/10">
//                 {isVideo ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}
//               </div>
//               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Accept</span>
//             </button>
//           </div>
//         </div>
//       )}

//       {/* 📡 --- OUTGOING CALL MODAL --- */}
//       {isCalling && !callActive && (
//         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in slide-in-from-bottom-20 duration-500">
//           <div className="relative">
//             <div className="absolute inset-0 bg-blue-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
//             <div className="w-32 h-32 rounded-full border-2 border-dashed border-blue-500/40 flex justify-center items-center animate-spin-slow">
//               <div className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex justify-center items-center text-5xl text-white font-black shadow-2xl">
//                 {targetUserName?.charAt(0).toUpperCase() || "U"}
//               </div>
//             </div>
//           </div>
//           <div className="text-center">
//             <h2 className="text-4xl font-black text-white tracking-tight mb-2">Connecting...</h2>
//             <p className="text-white/40 text-xs font-black uppercase tracking-widest italic">{targetUserName || "Secure User"}</p>
//           </div>
//           <button onClick={endCall} className="w-16 h-16 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-2xl flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/30">
//             <PhoneOff className="w-7 h-7" />
//           </button>
//         </div>
//       )}

//       {/* 🚀 --- ACTIVE CALL SCREEN --- */}
//       {callActive && (
//         <div className={`transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] z-[1000001] ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-[2rem] overflow-hidden shadow-2xl border border-white/20' : 'w-full h-full max-w-7xl mx-auto flex flex-col p-6'}`}>
//           <div className="flex-1 relative bg-[#08090a] rounded-[3rem] overflow-hidden shadow-[0_64px_128px_-20px_rgba(0,0,0,0.9)] border border-white/10">
//             <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
//               {isVideo ? (
//                 <video ref={remoteVideoRef} className="w-full h-full object-cover scale-[1.02]" autoPlay playsInline controls={false} />
//               ) : (
//                 <div className="flex flex-col items-center gap-10">
//                   <div className="relative">
//                     <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[80px] opacity-20 animate-pulse"></div>
//                     <div className="w-48 h-48 bg-gradient-to-tr from-[#1e1b4b] via-[#312e81] to-[#3730a3] rounded-full flex items-center justify-center text-8xl text-white font-black shadow-2xl border-4 border-white/10">
//                       {(callerName || targetUserName)?.charAt(0) || "U"}
//                     </div>
//                     <div className="absolute -bottom-2 right-1/2 translate-x-1/2 px-6 py-2 bg-indigo-600 rounded-2xl text-[10px] text-white font-black uppercase tracking-[0.3em] shadow-2xl border-4 border-[#08090a]">Secured</div>
//                   </div>
//                   <div className="text-center">
//                     <h3 className="text-5xl font-black text-white mb-2 tracking-tighter">{callerName || targetUserName || "User"}</h3>
//                     <p className="text-indigo-400 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">End-to-End Encrypted</p>
//                   </div>
//                   {/* <audio ref={remoteVideoRef} autoPlay /> */}
//                   <audio ref={remoteAudioRef} autoPlay />
//                 </div>
//               )}
//             </div>
//             {isVideo && !isMinimized && (
//               <div className="absolute top-10 right-10 w-48 h-64 bg-black/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 group cursor-move hover:border-indigo-500/50 transition-colors">
//                 <video ref={localVideoRef} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" autoPlay playsInline muted />
//                 <div className="absolute top-4 left-4 bg-black/40 px-2 py-0.5 rounded-lg text-[8px] text-white/60 font-black uppercase tracking-widest">You</div>
//               </div>
//             )}
//             {!isMinimized ? (
//               <button onClick={() => setIsMinimized(true)} className="absolute top-10 left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl transition-all border border-white/10 group">
//                 <Minimize2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
//               </button>
//             ) : (
//               <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
//                 <Maximize2 className="text-white w-12 h-12" />
//               </button>
//             )}
//           </div>
//           {!isMinimized && (
//             <div className="h-36 flex items-center justify-center gap-12 relative mt-4">
//               <div className="bg-[#12141c]/60 backdrop-blur-[48px] px-16 py-7 rounded-[3rem] border border-white/10 flex items-center gap-12 shadow-[0_24px_48px_rgba(0,0,0,0.6)]">
//                 <button onClick={toggleMute} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isMuted ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
//                   {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
//                 </button>
//                 <button onClick={endCall} className="w-20 h-20 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-[2rem] flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/40">
//                   <PhoneOff className="w-9 h-9" />
//                 </button>
//                 {isVideo && (
//                   <button onClick={toggleCamera} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isCameraOff ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
//                     {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
//                   </button>
//                 )}
//               </div>
//               <div className="absolute right-10 flex items-center gap-4 px-8 py-4 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-md">
//                 <div className="flex flex-col items-end">
//                   <span className="text-[10px] text-white/30 uppercase font-black tracking-widest leading-none mb-1">Channel</span>
//                   <span className="text-xs text-emerald-400 font-black tracking-tight">Active_P2P</span>
//                 </div>
//                 <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.8)]"></div>
//               </div>
//             </div>
//           )}
//         </div>
//       )}
//       <style>{`
//         @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
//         .animate-spin-slow { animation: spin-slow 20s linear infinite; }
//       `}</style>
//     </div>
//   );
// }





// // import { useEffect, useRef, useState, useCallback } from "react";
// // import { useCallStore } from "../app/callStore";
// // import { getSocket } from "../socket/socket";
// // import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2 } from "lucide-react";
// // import ringtoneSound from "../assets/VibrateCall.mp3";

// // export default function CallManager() {
// //   const {
// //     isReceivingCall,
// //     isCalling,
// //     callActive,
// //     callerName,
// //     callerSignal,
// //     callerId,
// //     targetUserId,
// //     targetUserName,
// //     isVideo,
// //     setCallState,
// //     clearCallState,
// //   } = useCallStore();


// //   const remoteAudioRef = useRef(null);
// //   const [localStream, setLocalStream] = useState(null);
// //   const [remoteStream, setRemoteStream] = useState(null);

// //   const [isMuted, setIsMuted] = useState(false);
// //   const [isCameraOff, setIsCameraOff] = useState(false);
// //   const [isMinimized, setIsMinimized] = useState(false);
// //   const [autoplayBlocked, setAutoplayBlocked] = useState(false);

// //   const localVideoRef = useRef(null);
// //   const remoteVideoRef = useRef(null);
// //   const connectionRef = useRef(null);
// //   const ringtoneRef = useRef(null);
// //   const vibrateInterval = useRef(null);
// //   const callInitiatedRef = useRef(false);

// //   // ==========================================
// //   // 🎵 HELPERS (useCallback to avoid hoisting errors)
// //   // ==========================================

// //   const startRinging = useCallback(() => {
// //     if (!ringtoneRef.current) {
// //       ringtoneRef.current = new Audio(ringtoneSound);
// //       ringtoneRef.current.loop = true;
// //     }
// //     ringtoneRef.current.play().then(() => {
// //       setAutoplayBlocked(false);
// //     }).catch(e => {
// //       console.log("Audio block:", e);
// //       setAutoplayBlocked(true);
// //     });
// //     if (navigator.vibrate) {
// //       vibrateInterval.current = setInterval(() => {
// //         navigator.vibrate([400, 200, 400]);
// //       }, 1200);
// //     }
// //   }, []);

// //   const stopRinging = useCallback(() => {
// //     if (ringtoneRef.current) {
// //       ringtoneRef.current.pause();
// //       ringtoneRef.current.currentTime = 0;
// //     }
// //     if (vibrateInterval.current) clearInterval(vibrateInterval.current);
// //     if (navigator.vibrate) navigator.vibrate(0);
// //   }, []);

// //   // const initStream = useCallback(async (videoRequired) => {
// //   //   try {
// //   //     const stream = await navigator.mediaDevices.getUserMedia({
// //   //       video: videoRequired ? { width: 1280, height: 720, facingMode: "user" } : false,
// //   //       audio: true
// //   //     });
// //   //     setLocalStream(stream);
// //   //     return stream;
// //   //   } catch (err) {
// //   //     console.error("Stream access denied:", err);
// //   //     return null;
// //   //   }
// //   // }, []);

// //   const initStream = useCallback(async () => {
// //     try {
// //       const stream = await navigator.mediaDevices.getUserMedia({
// //         video: true,
// //         audio: true
// //       });
  
// //       console.log("🎥 Tracks:", stream.getTracks());

// //       // ✅ FORCE ENABLE CAMERA
// // stream.getVideoTracks().forEach(track => {
// //   track.enabled = true;
// // });

  
// //       setLocalStream(stream);
  
// //       if (localVideoRef.current) {
// //         localVideoRef.current.srcObject = stream;
// //         localVideoRef.current.play().catch(() => {});
// //       }
  
// //       return stream;
// //     } catch (err) {
// //       console.error("Stream access denied:", err);
// //       return null;
// //     }
// //   }, []);


// //   const createPeer = useCallback((stream, socket, toUserId) => {
// //     const peer = new RTCPeerConnection({
// //       iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
// //     });
  
// //     // ✅ Add tracks
// //     stream.getTracks().forEach(track => {
// //       peer.addTrack(track, stream);
// //     });
  
// //     // ✅ FIX: handle remote stream properly
// //     peer.ontrack = (event) => {
// //       console.log("🎬 Remote track:", event.streams);
  
// //       if (event.streams && event.streams[0]) {
// //         setRemoteStream(event.streams[0]);
// //       }
// //     };
  
// //     // ✅ ICE
// //     peer.onicecandidate = (event) => {
// //       if (event.candidate) {
// //         socket.emit("ice_candidate", {
// //           to: toUserId,
// //           candidate: event.candidate
// //         });
// //       }
// //     };
  
// //     return peer;
// //   }, []);




// //   const endCallCleanup = useCallback(() => {
// //     // 📝 LOG CALL IN CHAT BEFORE CLEANUP
// //     if (callActive || isCalling) {
// //       const socket = getSocket();
// //       const targetId = callerId || targetUserId;
// //       const groupId = useCallStore.getState().groupId;
// //       if (socket && (targetId || groupId)) {
// //         socket.emit("send_message", {
// //           receiverId: targetId || null,
// //           groupId: groupId || null,
// //           message: `📞 ${isVideo ? 'Video' : 'Audio'} call ended`,
// //           type: "call_log", // Custom type for UI styling
// //           clientTime: new Date(),
// //         });
// //       }
// //     }

// //     // if (localStream) localStream.getTracks().forEach(track => track.stop());
// //     if (localStream) {
// //       localStream.getTracks().forEach(track => track.stop());
// //     }
    
// //     if (remoteStream) {
// //       remoteStream.getTracks().forEach(track => track.stop());
// //     }
// //     if (connectionRef.current) {
// //       connectionRef.current.close();
// //       connectionRef.current = null;
// //     }
// //     setLocalStream(null);
// //     setRemoteStream(null);
// //     clearCallState();
// //     stopRinging();
// //     callInitiatedRef.current = false;
// //   }, [callActive, isCalling, callerId, targetUserId, isVideo, localStream, clearCallState, stopRinging]);

// //   const initiateCallFlow = useCallback(async () => {
// //     const socket = getSocket();
// //     if (!socket) return;
// //     try {
// //       const stream = await initStream(isVideo);
// //       if (!stream) {
// //         clearCallState();
// //         return;
// //       }
// //       const peer = createPeer(stream, socket, targetUserId);
// //       connectionRef.current = peer;
// //       const offer = await peer.createOffer();
// //       await peer.setLocalDescription(offer);
// //       const user = JSON.parse(localStorage.getItem("user") || "{}");
// //       socket.emit("call_user", {
// //         userToCall: targetUserId || null,
// //         groupToCall: useCallStore.getState().groupId || null,
// //         signalData: offer,
// //         from: user.id,
// //         name: `${user.first_name} ${user.last_name || ""}`,
// //         isVideo: isVideo,
// //       });
// //     } catch (err) {
// //       console.error("Initiate error:", err);
// //       endCallCleanup();
// //     }
// //   }, [isVideo, targetUserId, clearCallState, initStream, createPeer, endCallCleanup]);

// //   // ==========================================
// //   // 🧨 EFFECTS
// //   // ==========================================

// //   useEffect(() => {
// //     const socket = getSocket();
// //     if (!socket) return;

// //     const handleIncomingCall = (data) => {
// //       console.log("☎️ Incoming call from:", data.from);
// //       setCallState({
// //         isReceivingCall: true,
// //         callerId: data.from,
// //         callerName: data.groupId ? `Group: ${data.name}` : data.name,
// //         callerSignal: data.signal,
// //         isVideo: data.isVideo,
// //         groupId: data.groupId || null,
// //       });
// //       startRinging();
// //     };

// //     const handleCallAccepted = async (signal) => {
// //       console.log("✅ Call accepted! Establishing connection...");
// //       stopRinging();
// //       setCallState({ callActive: true, isCalling: false });
// //       if (connectionRef.current) {
// //         try {
// //           await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
// //         } catch (e) {
// //           console.error("Error setting remote description:", e);
// //         }
// //       }
// //     };

// //     const handleCallRejected = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleCallEnded = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleIceCandidate = async (candidate) => {
// //       if (connectionRef.current && candidate) {
// //         try {
// //           await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
// //         } catch (e) {
// //           console.error("Error adding ice candidate:", e);
// //         }
// //       }
// //     };

// //     socket.on("incoming_call", handleIncomingCall);
// //     socket.on("call_accepted", handleCallAccepted);
// //     socket.on("call_rejected", handleCallRejected);
// //     socket.on("call_ended", handleCallEnded);
// //     socket.on("ice_candidate", handleIceCandidate);

// //     return () => {
// //       socket.off("incoming_call", handleIncomingCall);
// //       socket.off("call_accepted", handleCallAccepted);
// //       socket.off("call_rejected", handleCallRejected);
// //       socket.off("call_ended", handleCallEnded);
// //       socket.off("ice_candidate", handleIceCandidate);
// //     };
// //   }, [setCallState, stopRinging, startRinging, endCallCleanup]);

// //   useEffect(() => {
// //     if (isCalling && !callActive && !callInitiatedRef.current) {
// //       callInitiatedRef.current = true;
// //       initiateCallFlow();
// //     } else if (!isCalling && !callActive) {
// //       callInitiatedRef.current = false;
// //     }
// //   }, [isCalling, callActive, initiateCallFlow]);



 
// //   // useEffect(() => {
// //   //   if (localVideoRef.current && localStream) {
// //   //     localVideoRef.current.srcObject = localStream;
  
// //   //     const playPromise = localVideoRef.current.play();
// //   //     if (playPromise !== undefined) {
// //   //       playPromise.catch((err) => {
// //   //         console.log("❌ Local video autoplay blocked:", err);
// //   //       });
// //   //     }
// //   //   }
// //   // }, [localStream]);

// //   useEffect(() => {
// //     if (localVideoRef.current && localStream) {



// //           // 🔥 ADD HERE
// //     console.log(
// //       "LOCAL VIDEO TRACK ENABLED:",
// //       localStream?.getVideoTracks()[0]?.enabled
// //     );

// //       console.log("✅ Attaching LOCAL stream");
  
// //       localVideoRef.current.srcObject = localStream;
// //       localVideoRef.current.muted = true; // 🔥 important
// //       localVideoRef.current.playsInline = true;
  
// //       const playPromise = localVideoRef.current.play();
// //       if (playPromise !== undefined) {
// //         playPromise.catch((err) => {
// //           console.log("❌ Local video autoplay blocked:", err);
// //         });
// //       }
// //     }
// //   }, [localStream, callActive, isMinimized]);


// //   useEffect(() => {
// //     if (!remoteStream) return;
  
// //     console.log("🔊 Attaching remote stream...");
  
// //     // 🎥 VIDEO
// //     if (remoteVideoRef.current) {
// //       remoteVideoRef.current.srcObject = remoteStream;
// //       remoteVideoRef.current.play().catch(() => {});
// //     }
  
// //     // 🔊 AUDIO (CRITICAL FIX)
// //     if (remoteAudioRef.current) {
// //       remoteAudioRef.current.srcObject = remoteStream;
// //       remoteAudioRef.current.play().catch(() => {});
// //     }
  
// //   }, [remoteStream, callActive, isMinimized]);



// //   // ==========================================
// //   // 🎮 ACTIONS
// //   // ==========================================

// //   const answerCall = async () => {
// //     stopRinging();
// //     const stream = await initStream(isVideo);
// //     if (!stream) return;
// //     setCallState({ callActive: true, isReceivingCall: false });
// //     const socket = getSocket();
// //     const peer = createPeer(stream, socket, callerId);
// //     connectionRef.current = peer;
// //     try {
// //       await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
// //       const answer = await peer.createAnswer();
// //       await peer.setLocalDescription(answer);
// //       socket.emit("make_answer", { to: callerId, signal: answer });
// //     } catch (e) {
// //       console.error("Answer error:", e);
// //       endCallCleanup();
// //     }
// //   };

// //   const rejectCall = () => {
// //     stopRinging();
// //     const socket = getSocket();
// //     if (socket) socket.emit("reject_call", { to: callerId });
// //     clearCallState();
// //   };

// //   const endCall = () => {
// //     const socket = getSocket();
// //     const targetId = callerId || targetUserId;
// //     const groupId = useCallStore.getState().groupId;

// //     if (socket) {
// //       if (targetId) socket.emit("end_call", { to: targetId });
// //       else if (groupId) socket.emit("end_call", { groupId });
// //     }
// //     endCallCleanup();
// //   };

// //   const toggleMute = () => {
// //     if (localStream) {
// //       localStream.getAudioTracks().forEach(track => {
// //         track.enabled = !track.enabled;

// //         console.log("VIDEO TRACK ENABLED:", track.enabled);
// //         setIsMuted(!track.enabled);
// //       });
// //     }
// //   };

// //   // const toggleCamera = () => {
// //   //   if (localStream) {
// //   //     console.log("LOCAL STREAM TRACKS:", localStream.getVideoTracks());

// //   //     localStream.getVideoTracks().forEach(track => {
// //   //       track.enabled = !track.enabled;
// //   //       setIsCameraOff(!track.enabled);
// //   //     });
// //   //   }
// //   // };




// //   const toggleCamera = () => {
// //     if (!localStream) return;
  
// //     const videoTrack = localStream.getVideoTracks()[0];
// //     if (!videoTrack) return;
  
// //     const newState = !videoTrack.enabled;
// //     videoTrack.enabled = newState;
  
// //     console.log("🎥 Camera enabled:", newState);
  
// //     setIsCameraOff(!newState); // UI follows real state
// //   };

// //   if (!isCalling && !isReceivingCall && !callActive) return null;

// //   return (
// //     <div className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#050508]/70 backdrop-blur-[32px]'}`}>

// //       {/* 🔮 --- INCOMING CALL MODAL --- */}
// //       {isReceivingCall && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in zoom-in-95 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-indigo-600 via-purple-500 to-pink-500 animate-spin-slow">
// //               <div className="w-full h-full bg-[#12141c] rounded-full flex justify-center items-center text-6xl text-white font-black border-2 border-[#12141c]">
// //                 {callerName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //             <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-emerald-500 rounded-full border-4 border-[#12141c] flex items-center justify-center animate-bounce shadow-xl shadow-emerald-500/20">
// //               <Phone className="text-white w-5 h-5 fill-white" />
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-3">{callerName || "Unknown"}</h2>
// //             {autoplayBlocked && (
// //               <button
// //                 onClick={() => {
// //                   ringtoneRef.current?.play();
// //                   setAutoplayBlocked(false);
// //                 }}
// //                 className="mb-4 text-[10px] text-indigo-400 font-bold uppercase animate-pulse underline"
// //               >
// //                 Click to enable ringtone
// //               </button>
// //             )}
// //             <p className="text-purple-400 font-black uppercase tracking-[0.2em] text-[10px] bg-purple-500/10 px-4 py-1.5 rounded-full border border-purple-500/20">
// //               {isVideo ? "Incoming Video Request" : "Incoming Audio Request"}
// //             </p>
// //           </div>
// //           <div className="flex gap-12 mt-2">
// //             <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-red-500/10 hover:bg-red-500 rounded-2xl flex items-center justify-center text-red-500 hover:text-white transition-all duration-300 border border-red-500/20 shadow-lg shadow-red-500/10">
// //                 <PhoneOff className="w-7 h-7" />
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Decline</span>
// //             </button>
// //             <button onClick={answerCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-emerald-500/10 hover:bg-emerald-500 rounded-2xl flex items-center justify-center text-emerald-500 hover:text-white transition-all duration-300 border border-emerald-500/20 shadow-lg shadow-emerald-500/10">
// //                 {isVideo ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Accept</span>
// //             </button>
// //           </div>
// //         </div>
// //       )}

// //       {/* 📡 --- OUTGOING CALL MODAL --- */}
// //       {isCalling && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in slide-in-from-bottom-20 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-blue-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full border-2 border-dashed border-blue-500/40 flex justify-center items-center animate-spin-slow">
// //               <div className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex justify-center items-center text-5xl text-white font-black shadow-2xl">
// //                 {targetUserName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-2">Connecting...</h2>
// //             <p className="text-white/40 text-xs font-black uppercase tracking-widest italic">{targetUserName || "Secure User"}</p>
// //           </div>
// //           <button onClick={endCall} className="w-16 h-16 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-2xl flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/30">
// //             <PhoneOff className="w-7 h-7" />
// //           </button>
// //         </div>
// //       )}

// //       {/* 🚀 --- ACTIVE CALL SCREEN --- */}
// //       {callActive && (
// //         <div className={`transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] z-[1000001] ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-[2rem] overflow-hidden shadow-2xl border border-white/20' : 'w-full h-full max-w-7xl mx-auto flex flex-col p-6'}`}>
// //           <div className="flex-1 relative bg-[#08090a] rounded-[3rem] overflow-hidden shadow-[0_64px_128px_-20px_rgba(0,0,0,0.9)] border border-white/10">
// //             <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
// //               {isVideo ? (
// //                 <video ref={remoteVideoRef} className="w-full h-full object-cover scale-[1.02]" autoPlay playsInline controls={false} />
// //               ) : (
// //                 <div className="flex flex-col items-center gap-10">
// //                   <div className="relative">
// //                     <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[80px] opacity-20 animate-pulse"></div>
// //                     <div className="w-48 h-48 bg-gradient-to-tr from-[#1e1b4b] via-[#312e81] to-[#3730a3] rounded-full flex items-center justify-center text-8xl text-white font-black shadow-2xl border-4 border-white/10">
// //                       {(callerName || targetUserName)?.charAt(0) || "U"}
// //                     </div>
// //                     <div className="absolute -bottom-2 right-1/2 translate-x-1/2 px-6 py-2 bg-indigo-600 rounded-2xl text-[10px] text-white font-black uppercase tracking-[0.3em] shadow-2xl border-4 border-[#08090a]">Secured</div>
// //                   </div>
// //                   <div className="text-center">
// //                     <h3 className="text-5xl font-black text-white mb-2 tracking-tighter">{callerName || targetUserName || "User"}</h3>
// //                     <p className="text-indigo-400 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">End-to-End Encrypted</p>
// //                   </div>
// //                   {/* <audio ref={remoteVideoRef} autoPlay /> */}
// //                   <audio ref={remoteAudioRef} autoPlay />
// //                 </div>
// //               )}
// //             </div>
// //             {isVideo && !isMinimized && (
// //               <div className="absolute top-10 right-10 w-48 h-64 bg-black/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 group cursor-move hover:border-indigo-500/50 transition-colors">
              
// //                 <video ref={localVideoRef} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" autoPlay playsInline muted />
// //                 <div className="absolute top-4 left-4 bg-black/40 px-2 py-0.5 rounded-lg text-[8px] text-white/60 font-black uppercase tracking-widest">You</div>
// //               </div>
// //             )}
// //             {!isMinimized ? (
// //               <button onClick={() => setIsMinimized(true)} className="absolute top-10 left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl transition-all border border-white/10 group">
// //                 <Minimize2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
// //               </button>
// //             ) : (
// //               <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
// //                 <Maximize2 className="text-white w-12 h-12" />
// //               </button>
// //             )}
// //           </div>
// //           {!isMinimized && (
// //             <div className="h-36 flex items-center justify-center gap-12 relative mt-4">
// //               <div className="bg-[#12141c]/60 backdrop-blur-[48px] px-16 py-7 rounded-[3rem] border border-white/10 flex items-center gap-12 shadow-[0_24px_48px_rgba(0,0,0,0.6)]">
// //                 <button onClick={toggleMute} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isMuted ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                   {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
// //                 </button>
// //                 <button onClick={endCall} className="w-20 h-20 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-[2rem] flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/40">
// //                   <PhoneOff className="w-9 h-9" />
// //                 </button>
// //                 {isVideo && (
// //                   <button onClick={toggleCamera} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isCameraOff ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                     {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
// //                   </button>
// //                 )}
// //               </div>
// //               <div className="absolute right-10 flex items-center gap-4 px-8 py-4 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-md">
// //                 <div className="flex flex-col items-end">
// //                   <span className="text-[10px] text-white/30 uppercase font-black tracking-widest leading-none mb-1">Channel</span>
// //                   <span className="text-xs text-emerald-400 font-black tracking-tight">Active_P2P</span>
// //                 </div>
// //                 <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.8)]"></div>
// //               </div>
// //             </div>
// //           )}
// //         </div>
// //       )}
// //       <style>{`
// //         @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
// //         .animate-spin-slow { animation: spin-slow 20s linear infinite; }
// //       `}</style>
// //     </div>
// //   );
// // }










// // import { useEffect, useRef, useState, useCallback } from "react";
// // import { useCallStore } from "../app/callStore";
// // import { getSocket } from "../socket/socket";
// // import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2 } from "lucide-react";
// // import ringtoneSound from "../assets/VibrateCall.mp3";

// // export default function CallManager() {
// //   const {
// //     isReceivingCall,
// //     isCalling,
// //     callActive,
// //     callerName,
// //     callerSignal,
// //     callerId,
// //     targetUserId,
// //     targetUserName,
// //     isVideo,
// //     setCallState,
// //     clearCallState,
// //   } = useCallStore();


// //   const remoteAudioRef = useRef(null);
// //   const [localStream, setLocalStream] = useState(null);
// //   const [remoteStream, setRemoteStream] = useState(null);

// //   const [isMuted, setIsMuted] = useState(false);
// //   const [isCameraOff, setIsCameraOff] = useState(false);
// //   const [isMinimized, setIsMinimized] = useState(false);
// //   const [autoplayBlocked, setAutoplayBlocked] = useState(false);

// //   const localVideoRef = useRef(null);
// //   const remoteVideoRef = useRef(null);
// //   const connectionRef = useRef(null);
// //   const ringtoneRef = useRef(null);
// //   const vibrateInterval = useRef(null);
// //   const callInitiatedRef = useRef(false);

// //   // ==========================================
// //   // 🎵 HELPERS (useCallback to avoid hoisting errors)
// //   // ==========================================

// //   const startRinging = useCallback(() => {
// //     if (!ringtoneRef.current) {
// //       ringtoneRef.current = new Audio(ringtoneSound);
// //       ringtoneRef.current.loop = true;
// //     }
// //     ringtoneRef.current.play().then(() => {
// //       setAutoplayBlocked(false);
// //     }).catch(e => {
// //       console.log("Audio block:", e);
// //       setAutoplayBlocked(true);
// //     });
// //     if (navigator.vibrate) {
// //       vibrateInterval.current = setInterval(() => {
// //         navigator.vibrate([400, 200, 400]);
// //       }, 1200);
// //     }
// //   }, []);

// //   const stopRinging = useCallback(() => {
// //     if (ringtoneRef.current) {
// //       ringtoneRef.current.pause();
// //       ringtoneRef.current.currentTime = 0;
// //     }
// //     if (vibrateInterval.current) clearInterval(vibrateInterval.current);
// //     if (navigator.vibrate) navigator.vibrate(0);
// //   }, []);

// //   // const initStream = useCallback(async (videoRequired) => {
// //   //   try {
// //   //     const stream = await navigator.mediaDevices.getUserMedia({
// //   //       video: videoRequired ? { width: 1280, height: 720, facingMode: "user" } : false,
// //   //       audio: true
// //   //     });
// //   //     setLocalStream(stream);
// //   //     return stream;
// //   //   } catch (err) {
// //   //     console.error("Stream access denied:", err);
// //   //     return null;
// //   //   }
// //   // }, []);

// //   const initStream = useCallback(async () => {
// //     try {
// //       const stream = await navigator.mediaDevices.getUserMedia({
// //         video: true,
// //         audio: true
// //       });
  
// //       console.log("🎥 Tracks:", stream.getTracks());
  
// //       setLocalStream(stream);
  
// //       if (localVideoRef.current) {
// //         localVideoRef.current.srcObject = stream;
// //         localVideoRef.current.play().catch(() => {});
// //       }
  
// //       return stream;
// //     } catch (err) {
// //       console.error("Stream access denied:", err);
// //       return null;
// //     }
// //   }, []);


// //   const createPeer = useCallback((stream, socket, toUserId) => {
// //     const peer = new RTCPeerConnection({
// //       iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
// //     });
  
// //     // ✅ Add tracks
// //     stream.getTracks().forEach(track => {
// //       peer.addTrack(track, stream);
// //     });
  
// //     // ✅ FIX: handle remote stream properly
// //     peer.ontrack = (event) => {
// //       console.log("🎬 Remote track:", event.streams);
  
// //       if (event.streams && event.streams[0]) {
// //         setRemoteStream(event.streams[0]);
// //       }
// //     };
  
// //     // ✅ ICE
// //     peer.onicecandidate = (event) => {
// //       if (event.candidate) {
// //         socket.emit("ice_candidate", {
// //           to: toUserId,
// //           candidate: event.candidate
// //         });
// //       }
// //     };
  
// //     return peer;
// //   }, []);




// //   const endCallCleanup = useCallback(() => {
// //     // 📝 LOG CALL IN CHAT BEFORE CLEANUP
// //     if (callActive || isCalling) {
// //       const socket = getSocket();
// //       const targetId = callerId || targetUserId;
// //       const groupId = useCallStore.getState().groupId;
// //       if (socket && (targetId || groupId)) {
// //         socket.emit("send_message", {
// //           receiverId: targetId || null,
// //           groupId: groupId || null,
// //           message: `📞 ${isVideo ? 'Video' : 'Audio'} call ended`,
// //           type: "call_log", // Custom type for UI styling
// //           clientTime: new Date(),
// //         });
// //       }
// //     }

// //     // if (localStream) localStream.getTracks().forEach(track => track.stop());
// //     if (localStream) {
// //       localStream.getTracks().forEach(track => track.stop());
// //     }
    
// //     if (remoteStream) {
// //       remoteStream.getTracks().forEach(track => track.stop());
// //     }
// //     if (connectionRef.current) {
// //       connectionRef.current.close();
// //       connectionRef.current = null;
// //     }
// //     setLocalStream(null);
// //     setRemoteStream(null);
// //     clearCallState();
// //     stopRinging();
// //     callInitiatedRef.current = false;
// //   }, [callActive, isCalling, callerId, targetUserId, isVideo, localStream, clearCallState, stopRinging]);

// //   const initiateCallFlow = useCallback(async () => {
// //     const socket = getSocket();
// //     if (!socket) return;
// //     try {
// //       const stream = await initStream(isVideo);
// //       if (!stream) {
// //         clearCallState();
// //         return;
// //       }
// //       const peer = createPeer(stream, socket, targetUserId);
// //       connectionRef.current = peer;
// //       const offer = await peer.createOffer();
// //       await peer.setLocalDescription(offer);
// //       const user = JSON.parse(localStorage.getItem("user") || "{}");
// //       socket.emit("call_user", {
// //         userToCall: targetUserId || null,
// //         groupToCall: useCallStore.getState().groupId || null,
// //         signalData: offer,
// //         from: user.id,
// //         name: `${user.first_name} ${user.last_name || ""}`,
// //         isVideo: isVideo,
// //       });
// //     } catch (err) {
// //       console.error("Initiate error:", err);
// //       endCallCleanup();
// //     }
// //   }, [isVideo, targetUserId, clearCallState, initStream, createPeer, endCallCleanup]);

// //   // ==========================================
// //   // 🧨 EFFECTS
// //   // ==========================================

// //   useEffect(() => {
// //     const socket = getSocket();
// //     if (!socket) return;

// //     const handleIncomingCall = (data) => {
// //       console.log("☎️ Incoming call from:", data.from);
// //       setCallState({
// //         isReceivingCall: true,
// //         callerId: data.from,
// //         callerName: data.groupId ? `Group: ${data.name}` : data.name,
// //         callerSignal: data.signal,
// //         isVideo: data.isVideo,
// //         groupId: data.groupId || null,
// //       });
// //       startRinging();
// //     };

// //     const handleCallAccepted = async (signal) => {
// //       console.log("✅ Call accepted! Establishing connection...");
// //       stopRinging();
// //       setCallState({ callActive: true, isCalling: false });
// //       if (connectionRef.current) {
// //         try {
// //           await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
// //         } catch (e) {
// //           console.error("Error setting remote description:", e);
// //         }
// //       }
// //     };

// //     const handleCallRejected = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleCallEnded = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleIceCandidate = async (candidate) => {
// //       if (connectionRef.current && candidate) {
// //         try {
// //           await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
// //         } catch (e) {
// //           console.error("Error adding ice candidate:", e);
// //         }
// //       }
// //     };

// //     socket.on("incoming_call", handleIncomingCall);
// //     socket.on("call_accepted", handleCallAccepted);
// //     socket.on("call_rejected", handleCallRejected);
// //     socket.on("call_ended", handleCallEnded);
// //     socket.on("ice_candidate", handleIceCandidate);

// //     return () => {
// //       socket.off("incoming_call", handleIncomingCall);
// //       socket.off("call_accepted", handleCallAccepted);
// //       socket.off("call_rejected", handleCallRejected);
// //       socket.off("call_ended", handleCallEnded);
// //       socket.off("ice_candidate", handleIceCandidate);
// //     };
// //   }, [setCallState, stopRinging, startRinging, endCallCleanup]);

// //   useEffect(() => {
// //     if (isCalling && !callActive && !callInitiatedRef.current) {
// //       callInitiatedRef.current = true;
// //       initiateCallFlow();
// //     } else if (!isCalling && !callActive) {
// //       callInitiatedRef.current = false;
// //     }
// //   }, [isCalling, callActive, initiateCallFlow]);



 
// //   // useEffect(() => {
// //   //   if (localVideoRef.current && localStream) {
// //   //     localVideoRef.current.srcObject = localStream;
  
// //   //     const playPromise = localVideoRef.current.play();
// //   //     if (playPromise !== undefined) {
// //   //       playPromise.catch((err) => {
// //   //         console.log("❌ Local video autoplay blocked:", err);
// //   //       });
// //   //     }
// //   //   }
// //   // }, [localStream]);

// //   useEffect(() => {
// //     if (localVideoRef.current && localStream) {
// //       console.log("✅ Attaching LOCAL stream");
  
// //       localVideoRef.current.srcObject = localStream;
// //       localVideoRef.current.muted = true; // 🔥 important
// //       localVideoRef.current.playsInline = true;
  
// //       const playPromise = localVideoRef.current.play();
// //       if (playPromise !== undefined) {
// //         playPromise.catch((err) => {
// //           console.log("❌ Local video autoplay blocked:", err);
// //         });
// //       }
// //     }
// //   }, [localStream]);


// //   useEffect(() => {
// //     if (!remoteStream) return;
  
// //     console.log("🔊 Attaching remote stream...");
  
// //     // 🎥 VIDEO
// //     if (remoteVideoRef.current) {
// //       remoteVideoRef.current.srcObject = remoteStream;
// //       remoteVideoRef.current.play().catch(() => {});
// //     }
  
// //     // 🔊 AUDIO (CRITICAL FIX)
// //     if (remoteAudioRef.current) {
// //       remoteAudioRef.current.srcObject = remoteStream;
// //       remoteAudioRef.current.play().catch(() => {});
// //     }
  
// //   }, [remoteStream, callActive]);



// //   // ==========================================
// //   // 🎮 ACTIONS
// //   // ==========================================

// //   const answerCall = async () => {
// //     stopRinging();
// //     const stream = await initStream(isVideo);
// //     if (!stream) return;
// //     setCallState({ callActive: true, isReceivingCall: false });
// //     const socket = getSocket();
// //     const peer = createPeer(stream, socket, callerId);
// //     connectionRef.current = peer;
// //     try {
// //       await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
// //       const answer = await peer.createAnswer();
// //       await peer.setLocalDescription(answer);
// //       socket.emit("make_answer", { to: callerId, signal: answer });
// //     } catch (e) {
// //       console.error("Answer error:", e);
// //       endCallCleanup();
// //     }
// //   };

// //   const rejectCall = () => {
// //     stopRinging();
// //     const socket = getSocket();
// //     if (socket) socket.emit("reject_call", { to: callerId });
// //     clearCallState();
// //   };

// //   const endCall = () => {
// //     const socket = getSocket();
// //     const targetId = callerId || targetUserId;
// //     const groupId = useCallStore.getState().groupId;

// //     if (socket) {
// //       if (targetId) socket.emit("end_call", { to: targetId });
// //       else if (groupId) socket.emit("end_call", { groupId });
// //     }
// //     endCallCleanup();
// //   };

// //   const toggleMute = () => {
// //     if (localStream) {
// //       localStream.getAudioTracks().forEach(track => {
// //         track.enabled = !track.enabled;

// //         console.log("VIDEO TRACK ENABLED:", track.enabled);
// //         setIsMuted(!track.enabled);
// //       });
// //     }
// //   };

// //   const toggleCamera = () => {
// //     if (localStream) {
// //       console.log("LOCAL STREAM TRACKS:", localStream.getVideoTracks());

// //       localStream.getVideoTracks().forEach(track => {
// //         track.enabled = !track.enabled;
// //         setIsCameraOff(!track.enabled);
// //       });
// //     }
// //   };

// //   if (!isCalling && !isReceivingCall && !callActive) return null;

// //   return (
// //     <div className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#050508]/70 backdrop-blur-[32px]'}`}>

// //       {/* 🔮 --- INCOMING CALL MODAL --- */}
// //       {isReceivingCall && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in zoom-in-95 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-indigo-600 via-purple-500 to-pink-500 animate-spin-slow">
// //               <div className="w-full h-full bg-[#12141c] rounded-full flex justify-center items-center text-6xl text-white font-black border-2 border-[#12141c]">
// //                 {callerName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //             <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-emerald-500 rounded-full border-4 border-[#12141c] flex items-center justify-center animate-bounce shadow-xl shadow-emerald-500/20">
// //               <Phone className="text-white w-5 h-5 fill-white" />
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-3">{callerName || "Unknown"}</h2>
// //             {autoplayBlocked && (
// //               <button
// //                 onClick={() => {
// //                   ringtoneRef.current?.play();
// //                   setAutoplayBlocked(false);
// //                 }}
// //                 className="mb-4 text-[10px] text-indigo-400 font-bold uppercase animate-pulse underline"
// //               >
// //                 Click to enable ringtone
// //               </button>
// //             )}
// //             <p className="text-purple-400 font-black uppercase tracking-[0.2em] text-[10px] bg-purple-500/10 px-4 py-1.5 rounded-full border border-purple-500/20">
// //               {isVideo ? "Incoming Video Request" : "Incoming Audio Request"}
// //             </p>
// //           </div>
// //           <div className="flex gap-12 mt-2">
// //             <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-red-500/10 hover:bg-red-500 rounded-2xl flex items-center justify-center text-red-500 hover:text-white transition-all duration-300 border border-red-500/20 shadow-lg shadow-red-500/10">
// //                 <PhoneOff className="w-7 h-7" />
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Decline</span>
// //             </button>
// //             <button onClick={answerCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-emerald-500/10 hover:bg-emerald-500 rounded-2xl flex items-center justify-center text-emerald-500 hover:text-white transition-all duration-300 border border-emerald-500/20 shadow-lg shadow-emerald-500/10">
// //                 {isVideo ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Accept</span>
// //             </button>
// //           </div>
// //         </div>
// //       )}

// //       {/* 📡 --- OUTGOING CALL MODAL --- */}
// //       {isCalling && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in slide-in-from-bottom-20 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-blue-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full border-2 border-dashed border-blue-500/40 flex justify-center items-center animate-spin-slow">
// //               <div className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex justify-center items-center text-5xl text-white font-black shadow-2xl">
// //                 {targetUserName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-2">Connecting...</h2>
// //             <p className="text-white/40 text-xs font-black uppercase tracking-widest italic">{targetUserName || "Secure User"}</p>
// //           </div>
// //           <button onClick={endCall} className="w-16 h-16 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-2xl flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/30">
// //             <PhoneOff className="w-7 h-7" />
// //           </button>
// //         </div>
// //       )}

// //       {/* 🚀 --- ACTIVE CALL SCREEN --- */}
// //       {callActive && (
// //         <div className={`transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] z-[1000001] ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-[2rem] overflow-hidden shadow-2xl border border-white/20' : 'w-full h-full max-w-7xl mx-auto flex flex-col p-6'}`}>
// //           <div className="flex-1 relative bg-[#08090a] rounded-[3rem] overflow-hidden shadow-[0_64px_128px_-20px_rgba(0,0,0,0.9)] border border-white/10">
// //             <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
// //               {isVideo ? (
// //                 <video ref={remoteVideoRef} className="w-full h-full object-cover scale-[1.02]" autoPlay playsInline controls={false} />
// //               ) : (
// //                 <div className="flex flex-col items-center gap-10">
// //                   <div className="relative">
// //                     <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[80px] opacity-20 animate-pulse"></div>
// //                     <div className="w-48 h-48 bg-gradient-to-tr from-[#1e1b4b] via-[#312e81] to-[#3730a3] rounded-full flex items-center justify-center text-8xl text-white font-black shadow-2xl border-4 border-white/10">
// //                       {(callerName || targetUserName)?.charAt(0) || "U"}
// //                     </div>
// //                     <div className="absolute -bottom-2 right-1/2 translate-x-1/2 px-6 py-2 bg-indigo-600 rounded-2xl text-[10px] text-white font-black uppercase tracking-[0.3em] shadow-2xl border-4 border-[#08090a]">Secured</div>
// //                   </div>
// //                   <div className="text-center">
// //                     <h3 className="text-5xl font-black text-white mb-2 tracking-tighter">{callerName || targetUserName || "User"}</h3>
// //                     <p className="text-indigo-400 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">End-to-End Encrypted</p>
// //                   </div>
// //                   {/* <audio ref={remoteVideoRef} autoPlay /> */}
// //                   <audio ref={remoteAudioRef} autoPlay />
// //                 </div>
// //               )}
// //             </div>
// //             {isVideo && !isMinimized && (
// //               <div className="absolute top-10 right-10 w-48 h-64 bg-black/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 group cursor-move hover:border-indigo-500/50 transition-colors">
              
// // {/* <div className="absolute top-10 right-10 w-48 h-64 overflow-hidden rounded-[2.5rem] border border-white/20">                <video ref={localVideoRef} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" autoPlay playsInline muted /> */}
// //                 <div className="absolute top-4 left-4 bg-black/40 px-2 py-0.5 rounded-lg text-[8px] text-white/60 font-black uppercase tracking-widest">You</div>
// //               </div>
// //             )}
// //             {!isMinimized ? (
// //               <button onClick={() => setIsMinimized(true)} className="absolute top-10 left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl transition-all border border-white/10 group">
// //                 <Minimize2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
// //               </button>
// //             ) : (
// //               <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
// //                 <Maximize2 className="text-white w-12 h-12" />
// //               </button>
// //             )}
// //           </div>
// //           {!isMinimized && (
// //             <div className="h-36 flex items-center justify-center gap-12 relative mt-4">
// //               <div className="bg-[#12141c]/60 backdrop-blur-[48px] px-16 py-7 rounded-[3rem] border border-white/10 flex items-center gap-12 shadow-[0_24px_48px_rgba(0,0,0,0.6)]">
// //                 <button onClick={toggleMute} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isMuted ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                   {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
// //                 </button>
// //                 <button onClick={endCall} className="w-20 h-20 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-[2rem] flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/40">
// //                   <PhoneOff className="w-9 h-9" />
// //                 </button>
// //                 {isVideo && (
// //                   <button onClick={toggleCamera} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isCameraOff ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                     {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
// //                   </button>
// //                 )}
// //               </div>
// //               <div className="absolute right-10 flex items-center gap-4 px-8 py-4 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-md">
// //                 <div className="flex flex-col items-end">
// //                   <span className="text-[10px] text-white/30 uppercase font-black tracking-widest leading-none mb-1">Channel</span>
// //                   <span className="text-xs text-emerald-400 font-black tracking-tight">Active_P2P</span>
// //                 </div>
// //                 <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.8)]"></div>
// //               </div>
// //             </div>
// //           )}
// //         </div>
// //       )}
// //       <style>{`
// //         @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
// //         .animate-spin-slow { animation: spin-slow 20s linear infinite; }
// //       `}</style>
// //     </div>
// //   );
// // }





// // import { useEffect, useRef, useState, useCallback } from "react";
// // import { useCallStore } from "../app/callStore";
// // import { getSocket } from "../socket/socket";
// // import { Phone, Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Maximize2 } from "lucide-react";
// // import ringtoneSound from "../assets/VibrateCall.mp3";

// // export default function CallManager() {
// //   const {
// //     isReceivingCall,
// //     isCalling,
// //     callActive,
// //     callerName,
// //     callerSignal,
// //     callerId,
// //     targetUserId,
// //     targetUserName,
// //     isVideo,
// //     setCallState,
// //     clearCallState,
// //   } = useCallStore();

// //   const [localStream, setLocalStream] = useState(null);
// //   const [remoteStream, setRemoteStream] = useState(null);

// //   const [isMuted, setIsMuted] = useState(false);
// //   const [isCameraOff, setIsCameraOff] = useState(false);
// //   const [isMinimized, setIsMinimized] = useState(false);
// //   const [autoplayBlocked, setAutoplayBlocked] = useState(false);

// //   const localVideoRef = useRef(null);
// //   const remoteVideoRef = useRef(null);
// //   const connectionRef = useRef(null);
// //   const ringtoneRef = useRef(null);
// //   const vibrateInterval = useRef(null);
// //   const callInitiatedRef = useRef(false);

// //   // ==========================================
// //   // 🎵 HELPERS (useCallback to avoid hoisting errors)
// //   // ==========================================

// //   const startRinging = useCallback(() => {
// //     if (!ringtoneRef.current) {
// //       ringtoneRef.current = new Audio(ringtoneSound);
// //       ringtoneRef.current.loop = true;
// //     }
// //     ringtoneRef.current.play().then(() => {
// //       setAutoplayBlocked(false);
// //     }).catch(e => {
// //       console.log("Audio block:", e);
// //       setAutoplayBlocked(true);
// //     });
// //     if (navigator.vibrate) {
// //       vibrateInterval.current = setInterval(() => {
// //         navigator.vibrate([400, 200, 400]);
// //       }, 1200);
// //     }
// //   }, []);

// //   const stopRinging = useCallback(() => {
// //     if (ringtoneRef.current) {
// //       ringtoneRef.current.pause();
// //       ringtoneRef.current.currentTime = 0;
// //     }
// //     if (vibrateInterval.current) clearInterval(vibrateInterval.current);
// //     if (navigator.vibrate) navigator.vibrate(0);
// //   }, []);

// //   const initStream = useCallback(async (videoRequired) => {
// //     try {
// //       const stream = await navigator.mediaDevices.getUserMedia({
// //         video: videoRequired ? { width: 1280, height: 720, facingMode: "user" } : false,
// //         audio: true
// //       });
// //       setLocalStream(stream);
// //       return stream;
// //     } catch (err) {
// //       console.error("Stream access denied:", err);
// //       return null;
// //     }
// //   }, []);

// //   const createPeer = useCallback((stream, socket, toUserId) => {
// //     const peer = new RTCPeerConnection({
// //       iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
// //     });
// //     stream.getTracks().forEach((track) => peer.addTrack(track, stream));
// //     peer.ontrack = (event) => {
// //       console.log("🎬 Remote track received:", event.streams[0]);
// //       setRemoteStream(event.streams[0]);
// //     };
// //     peer.onicecandidate = (event) => {
// //       if (event.candidate) {
// //         socket.emit("ice_candidate", { to: toUserId, candidate: event.candidate });
// //       }
// //     };
// //     return peer;
// //   }, []);

// //   const endCallCleanup = useCallback(() => {
// //     // 📝 LOG CALL IN CHAT BEFORE CLEANUP
// //     if (callActive || isCalling) {
// //       const socket = getSocket();
// //       const targetId = callerId || targetUserId;
// //       const groupId = useCallStore.getState().groupId;
// //       if (socket && (targetId || groupId)) {
// //         socket.emit("send_message", {
// //           receiverId: targetId || null,
// //           groupId: groupId || null,
// //           message: `📞 ${isVideo ? 'Video' : 'Audio'} call ended`,
// //           type: "call_log", // Custom type for UI styling
// //           clientTime: new Date(),
// //         });
// //       }
// //     }

// //     if (localStream) localStream.getTracks().forEach(track => track.stop());
// //     if (connectionRef.current) {
// //       connectionRef.current.close();
// //       connectionRef.current = null;
// //     }
// //     setLocalStream(null);
// //     setRemoteStream(null);
// //     clearCallState();
// //     stopRinging();
// //     callInitiatedRef.current = false;
// //   }, [callActive, isCalling, callerId, targetUserId, isVideo, localStream, clearCallState, stopRinging]);

// //   const initiateCallFlow = useCallback(async () => {
// //     const socket = getSocket();
// //     if (!socket) return;
// //     try {
// //       const stream = await initStream(isVideo);
// //       if (!stream) {
// //         clearCallState();
// //         return;
// //       }
// //       const peer = createPeer(stream, socket, targetUserId);
// //       connectionRef.current = peer;
// //       const offer = await peer.createOffer();
// //       await peer.setLocalDescription(offer);
// //       const user = JSON.parse(localStorage.getItem("user") || "{}");
// //       socket.emit("call_user", {
// //         userToCall: targetUserId || null,
// //         groupToCall: useCallStore.getState().groupId || null,
// //         signalData: offer,
// //         from: user.id,
// //         name: `${user.first_name} ${user.last_name || ""}`,
// //         isVideo: isVideo,
// //       });
// //     } catch (err) {
// //       console.error("Initiate error:", err);
// //       endCallCleanup();
// //     }
// //   }, [isVideo, targetUserId, clearCallState, initStream, createPeer, endCallCleanup]);

// //   // ==========================================
// //   // 🧨 EFFECTS
// //   // ==========================================

// //   useEffect(() => {
// //     const socket = getSocket();
// //     if (!socket) return;

// //     const handleIncomingCall = (data) => {
// //       console.log("☎️ Incoming call from:", data.from);
// //       setCallState({
// //         isReceivingCall: true,
// //         callerId: data.from,
// //         callerName: data.groupId ? `Group: ${data.name}` : data.name,
// //         callerSignal: data.signal,
// //         isVideo: data.isVideo,
// //         groupId: data.groupId || null,
// //       });
// //       startRinging();
// //     };

// //     const handleCallAccepted = async (signal) => {
// //       console.log("✅ Call accepted! Establishing connection...");
// //       stopRinging();
// //       setCallState({ callActive: true, isCalling: false });
// //       if (connectionRef.current) {
// //         try {
// //           await connectionRef.current.setRemoteDescription(new RTCSessionDescription(signal));
// //         } catch (e) {
// //           console.error("Error setting remote description:", e);
// //         }
// //       }
// //     };

// //     const handleCallRejected = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleCallEnded = () => {
// //       stopRinging();
// //       endCallCleanup();
// //     };

// //     const handleIceCandidate = async (candidate) => {
// //       if (connectionRef.current && candidate) {
// //         try {
// //           await connectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
// //         } catch (e) {
// //           console.error("Error adding ice candidate:", e);
// //         }
// //       }
// //     };

// //     socket.on("incoming_call", handleIncomingCall);
// //     socket.on("call_accepted", handleCallAccepted);
// //     socket.on("call_rejected", handleCallRejected);
// //     socket.on("call_ended", handleCallEnded);
// //     socket.on("ice_candidate", handleIceCandidate);

// //     return () => {
// //       socket.off("incoming_call", handleIncomingCall);
// //       socket.off("call_accepted", handleCallAccepted);
// //       socket.off("call_rejected", handleCallRejected);
// //       socket.off("call_ended", handleCallEnded);
// //       socket.off("ice_candidate", handleIceCandidate);
// //     };
// //   }, [setCallState, stopRinging, startRinging, endCallCleanup]);

// //   useEffect(() => {
// //     if (isCalling && !callActive && !callInitiatedRef.current) {
// //       callInitiatedRef.current = true;
// //       initiateCallFlow();
// //     } else if (!isCalling && !callActive) {
// //       callInitiatedRef.current = false;
// //     }
// //   }, [isCalling, callActive, initiateCallFlow]);

// //   useEffect(() => {
// //     if (localVideoRef.current && localStream) {
// //       localVideoRef.current.srcObject = localStream;
// //     }
// //   }, [localStream]);

// //   useEffect(() => {
// //     if (remoteVideoRef.current && remoteStream) {
// //       console.log("🔊 Attaching remote stream to element...");
// //       remoteVideoRef.current.srcObject = remoteStream;

// //       const playPromise = remoteVideoRef.current.play();
// //       if (playPromise !== undefined) {
// //         playPromise.catch(error => {
// //           console.log("⚠️ Remote media playback blocked by browser:", error);
// //         });
// //       }
// //     }
// //   }, [remoteStream, callActive]);

// //   // ==========================================
// //   // 🎮 ACTIONS
// //   // ==========================================

// //   const answerCall = async () => {
// //     stopRinging();
// //     const stream = await initStream(isVideo);
// //     if (!stream) return;
// //     setCallState({ callActive: true, isReceivingCall: false });
// //     const socket = getSocket();
// //     const peer = createPeer(stream, socket, callerId);
// //     connectionRef.current = peer;
// //     try {
// //       await peer.setRemoteDescription(new RTCSessionDescription(callerSignal));
// //       const answer = await peer.createAnswer();
// //       await peer.setLocalDescription(answer);
// //       socket.emit("make_answer", { to: callerId, signal: answer });
// //     } catch (e) {
// //       console.error("Answer error:", e);
// //       endCallCleanup();
// //     }
// //   };

// //   const rejectCall = () => {
// //     stopRinging();
// //     const socket = getSocket();
// //     if (socket) socket.emit("reject_call", { to: callerId });
// //     clearCallState();
// //   };

// //   const endCall = () => {
// //     const socket = getSocket();
// //     const targetId = callerId || targetUserId;
// //     const groupId = useCallStore.getState().groupId;

// //     if (socket) {
// //       if (targetId) socket.emit("end_call", { to: targetId });
// //       else if (groupId) socket.emit("end_call", { groupId });
// //     }
// //     endCallCleanup();
// //   };

// //   const toggleMute = () => {
// //     if (localStream) {
// //       localStream.getAudioTracks().forEach(track => {
// //         track.enabled = !track.enabled;
// //         setIsMuted(!track.enabled);
// //       });
// //     }
// //   };

// //   const toggleCamera = () => {
// //     if (localStream) {
// //       localStream.getVideoTracks().forEach(track => {
// //         track.enabled = !track.enabled;
// //         setIsCameraOff(!track.enabled);
// //       });
// //     }
// //   };

// //   if (!isCalling && !isReceivingCall && !callActive) return null;

// //   return (
// //     <div className={`fixed inset-0 z-[2000000] transition-all duration-700 flex items-center justify-center ${isMinimized ? 'pointer-events-none' : 'bg-[#050508]/70 backdrop-blur-[32px]'}`}>

// //       {/* 🔮 --- INCOMING CALL MODAL --- */}
// //       {isReceivingCall && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in zoom-in-95 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-indigo-600 via-purple-500 to-pink-500 animate-spin-slow">
// //               <div className="w-full h-full bg-[#12141c] rounded-full flex justify-center items-center text-6xl text-white font-black border-2 border-[#12141c]">
// //                 {callerName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //             <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-emerald-500 rounded-full border-4 border-[#12141c] flex items-center justify-center animate-bounce shadow-xl shadow-emerald-500/20">
// //               <Phone className="text-white w-5 h-5 fill-white" />
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-3">{callerName || "Unknown"}</h2>
// //             {autoplayBlocked && (
// //               <button
// //                 onClick={() => {
// //                   ringtoneRef.current?.play();
// //                   setAutoplayBlocked(false);
// //                 }}
// //                 className="mb-4 text-[10px] text-indigo-400 font-bold uppercase animate-pulse underline"
// //               >
// //                 Click to enable ringtone
// //               </button>
// //             )}
// //             <p className="text-purple-400 font-black uppercase tracking-[0.2em] text-[10px] bg-purple-500/10 px-4 py-1.5 rounded-full border border-purple-500/20">
// //               {isVideo ? "Incoming Video Request" : "Incoming Audio Request"}
// //             </p>
// //           </div>
// //           <div className="flex gap-12 mt-2">
// //             <button onClick={rejectCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-red-500/10 hover:bg-red-500 rounded-2xl flex items-center justify-center text-red-500 hover:text-white transition-all duration-300 border border-red-500/20 shadow-lg shadow-red-500/10">
// //                 <PhoneOff className="w-7 h-7" />
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Decline</span>
// //             </button>
// //             <button onClick={answerCall} className="group flex flex-col items-center gap-3">
// //               <div className="w-16 h-16 bg-emerald-500/10 hover:bg-emerald-500 rounded-2xl flex items-center justify-center text-emerald-500 hover:text-white transition-all duration-300 border border-emerald-500/20 shadow-lg shadow-emerald-500/10">
// //                 {isVideo ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}
// //               </div>
// //               <span className="text-[10px] text-white/40 uppercase font-black tracking-widest">Accept</span>
// //             </button>
// //           </div>
// //         </div>
// //       )}

// //       {/* 📡 --- OUTGOING CALL MODAL --- */}
// //       {isCalling && !callActive && (
// //         <div className="bg-[#12141c]/90 border border-white/10 rounded-[3rem] p-12 flex flex-col items-center gap-8 shadow-[0_32px_128px_-16px_rgba(0,0,0,0.9)] w-[400px] animate-in slide-in-from-bottom-20 duration-500">
// //           <div className="relative">
// //             <div className="absolute inset-0 bg-blue-500 rounded-full blur-[50px] opacity-30 animate-pulse"></div>
// //             <div className="w-32 h-32 rounded-full border-2 border-dashed border-blue-500/40 flex justify-center items-center animate-spin-slow">
// //               <div className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex justify-center items-center text-5xl text-white font-black shadow-2xl">
// //                 {targetUserName?.charAt(0).toUpperCase() || "U"}
// //               </div>
// //             </div>
// //           </div>
// //           <div className="text-center">
// //             <h2 className="text-4xl font-black text-white tracking-tight mb-2">Connecting...</h2>
// //             <p className="text-white/40 text-xs font-black uppercase tracking-widest italic">{targetUserName || "Secure User"}</p>
// //           </div>
// //           <button onClick={endCall} className="w-16 h-16 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-2xl flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/30">
// //             <PhoneOff className="w-7 h-7" />
// //           </button>
// //         </div>
// //       )}

// //       {/* 🚀 --- ACTIVE CALL SCREEN --- */}
// //       {callActive && (
// //         <div className={`transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] z-[1000001] ${isMinimized ? 'fixed bottom-8 right-8 w-80 h-48 pointer-events-auto rounded-[2rem] overflow-hidden shadow-2xl border border-white/20' : 'w-full h-full max-w-7xl mx-auto flex flex-col p-6'}`}>
// //           <div className="flex-1 relative bg-[#08090a] rounded-[3rem] overflow-hidden shadow-[0_64px_128px_-20px_rgba(0,0,0,0.9)] border border-white/10">
// //             <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
// //               {isVideo ? (
// //                 <video ref={remoteVideoRef} className="w-full h-full object-cover scale-[1.02]" autoPlay playsInline />
// //               ) : (
// //                 <div className="flex flex-col items-center gap-10">
// //                   <div className="relative">
// //                     <div className="absolute inset-0 bg-indigo-500 rounded-full blur-[80px] opacity-20 animate-pulse"></div>
// //                     <div className="w-48 h-48 bg-gradient-to-tr from-[#1e1b4b] via-[#312e81] to-[#3730a3] rounded-full flex items-center justify-center text-8xl text-white font-black shadow-2xl border-4 border-white/10">
// //                       {(callerName || targetUserName)?.charAt(0) || "U"}
// //                     </div>
// //                     <div className="absolute -bottom-2 right-1/2 translate-x-1/2 px-6 py-2 bg-indigo-600 rounded-2xl text-[10px] text-white font-black uppercase tracking-[0.3em] shadow-2xl border-4 border-[#08090a]">Secured</div>
// //                   </div>
// //                   <div className="text-center">
// //                     <h3 className="text-5xl font-black text-white mb-2 tracking-tighter">{callerName || targetUserName || "User"}</h3>
// //                     <p className="text-indigo-400 font-black uppercase tracking-[0.3em] text-[10px] animate-pulse">End-to-End Encrypted</p>
// //                   </div>
// //                   <audio ref={remoteVideoRef} autoPlay />
// //                 </div>
// //               )}
// //             </div>
// //             {isVideo && !isMinimized && (
//               // <div className="absolute top-10 right-10 w-48 h-64 bg-black/40 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 group cursor-move hover:border-indigo-500/50 transition-colors">
// //                 <video ref={localVideoRef} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" autoPlay playsInline muted />
// //                 <div className="absolute top-4 left-4 bg-black/40 px-2 py-0.5 rounded-lg text-[8px] text-white/60 font-black uppercase tracking-widest">You</div>
// //               </div>
// //             )}
// //             {!isMinimized ? (
// //               <button onClick={() => setIsMinimized(true)} className="absolute top-10 left-10 p-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl transition-all border border-white/10 group">
// //                 <Minimize2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
// //               </button>
// //             ) : (
// //               <button onClick={() => setIsMinimized(false)} className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity z-10">
// //                 <Maximize2 className="text-white w-12 h-12" />
// //               </button>
// //             )}
// //           </div>
// //           {!isMinimized && (
// //             <div className="h-36 flex items-center justify-center gap-12 relative mt-4">
// //               <div className="bg-[#12141c]/60 backdrop-blur-[48px] px-16 py-7 rounded-[3rem] border border-white/10 flex items-center gap-12 shadow-[0_24px_48px_rgba(0,0,0,0.6)]">
// //                 <button onClick={toggleMute} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isMuted ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                   {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
// //                 </button>
// //                 <button onClick={endCall} className="w-20 h-20 bg-red-600 hover:bg-red-700 hover:rotate-90 rounded-[2rem] flex items-center justify-center text-white transition-all duration-500 shadow-2xl shadow-red-600/40">
// //                   <PhoneOff className="w-9 h-9" />
// //                 </button>
// //                 {isVideo && (
// //                   <button onClick={toggleCamera} className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${isCameraOff ? 'bg-red-500 text-white scale-110' : 'bg-white/5 text-white hover:bg-white/15'}`}>
// //                     {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
// //                   </button>
// //                 )}
// //               </div>
// //               <div className="absolute right-10 flex items-center gap-4 px-8 py-4 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-md">
// //                 <div className="flex flex-col items-end">
// //                   <span className="text-[10px] text-white/30 uppercase font-black tracking-widest leading-none mb-1">Channel</span>
// //                   <span className="text-xs text-emerald-400 font-black tracking-tight">Active_P2P</span>
// //                 </div>
// //                 <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.8)]"></div>
// //               </div>
// //             </div>
// //           )}
// //         </div>
// //       )}
// //       <style>{`
// //         @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
// //         .animate-spin-slow { animation: spin-slow 20s linear infinite; }
// //       `}</style>
// //     </div>
// //   );
// // }
